import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  Table,
  Input,
  Button,
  Typography,
  Space,
  Select,
  message,
  Tooltip,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  DownloadOutlined,
  ArrowRightOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import { useTender } from "../../../contexts/TenderContext";
import { useAuth } from "../../../hooks/useAuth";
import { useScrollToTop } from "../../../hooks/useScrollToTop";
import MainLayout from "../../layout/MainLayout";
import { useExcelImportExport } from "../../../hooks/useExcelImportExport";
import { ExcelImportExport } from "../../common/ExcelImportExport";
import TextInput from "../../common/TextInput";
import BufferedTextInput from "../../common/BufferedTextInput";
import { isDuplicatePlotNo } from "../../../utils/plotNoValidation";
import { ExcelColumn } from "../../../utils/excelUtils";
import { COLORS, EVALUATE_CODES, EvaluateCode } from "../../../utils/constants";
import { useNavigate } from "react-router-dom";
import { prepareTenderForAPI } from "../../../utils/storage";
import { useCreateBillOfQuantities } from "../../../hooks/useBillOfQuantities";
import NumberInput from "../../common/NumberInput";
// import { useChangeDetection } from "../../../hooks/useChangeDetection";
import { useTableRenderDeferral } from "../../../hooks/useTableRenderDeferral";
import TableLoadingPlaceholder from "../../common/TableLoadingPlaceholder";
import DeleteConfirmationModal from "../../common/DeleteConfirmationModal";
import {
  ColumnConfigModal,
  ConfigSection,
  ConfigField,
  selectStyles,
} from "../../common/ColumnConfigModal";
import { useGroundworksTotalCalculations } from "../../../hooks/useGroundworksTotalCalculations";
import { forceSaveBothBoqEndpoints } from "../../../utils/boqVersionUtils";
import { useItemLibrary } from "../../../hooks/useItemLibrary";
import { addColumnCodes as addColumnCodesBase, type CodeSelectConfig } from "@/utils/addColumnCodes";
import { PLOT_DRIVES_COL_CODES } from "@/utils/plotDrivesReferenceMapping";
import { normaliseDecimalMmInLabel } from "../../../utils/dimensionLabel";

// Maps label strings stored by handleSelectChange → ALP codes for sub-header display
const LABEL_TO_ALP_CODE_MAP: Record<string, string> = {
  '100mm thick AC 32mm dense bin': 'ALP041',
  '150mm thick AC 32mm dense bin': 'ALP042',
  '50mm thick AC 20mm dense bin':  'ALP043',
  '60mm thick AC 20mm dense bin':  'ALP044',
  '75mm thick AC 20mm dense bin':  'ALP045',
  '80mm thick AC 20mm dense bin':  'ALP046',
  '100mm thick AC 20mm dense bin': 'ALP047',
};

const { Title, Text } = Typography;
const { Option } = Select;

const excelColumns: ExcelColumn[] = [
  { key: "key", title: "Key" },
  { key: "plotNo", title: "Plot No" },
  { key: "housetype", title: "Housetype" },
  { key: "col1", title: "Level & Compact" },
  { key: "col2", title: "Geotextile Layer" },
  { key: "col3", title: "xxxmm thick MOT Type 1" },
  { key: "col4", title: "xxmm DB  Base" },
  { key: "col5", title: "xxmm DB Binder" },
  { key: "col6", title: "Sealing Grit" },
  { key: "col7", title: "Level & Compact" },
  { key: "col8", title: "Geotextile Layer" },
  { key: "col9", title: "xxxmm thick MOT Type 1" },
  { key: "col10", title: "xxmm DB Base" },
  { key: "col11", title: "xxmm DB Binder" },
  { key: "col12", title: "Sealing Grit" },
  { key: "col14", title: "Level & Compact" },
  { key: "col15", title: "Impermeable Geotextile Layer" },
  { key: "col16", title: "xxmm DB binder Running course" },
  { key: "col17", title: "xxmm thick 63-10mm Sub Base - Varied Depths" },
  { key: "col18", title: "xxmm thick 5-20mm clean aggregate sub base" },
  { key: "col19", title: "Permeable Geotextile Layer" },
  { key: "col20", title: "Level & Compact" },
  { key: "col21", title: "Geotextile Layer" },
  { key: "col22", title: "xxxmm thick MOT Type 1" },
  { key: "col23", title: "xxmm DB  Base" },
  { key: "col24", title: "xxmm DB Binder" },
  { key: "col25", title: "Sealing Grit" },
  { key: "col26", title: "Level & Compact" },
  { key: "col27", title: "Geotextile Layer" },
  { key: "col28", title: "xxxmm thick MOT Type 1" },
  { key: "col29", title: "xxmm DB Base" },
  { key: "col30", title: "xxmm DB Binder" },
  { key: "col31", title: "Sealing Grit" },
  { key: "col33", title: "Level & Compact" },
  { key: "col34", title: "Impermeable Geotextile Layer" },
  { key: "col35", title: "xxmm DB binder Running course" },
  { key: "col36", title: "xxmm thick 63-10mm Sub Base - Varied Depths" },
  { key: "col37", title: "xxmm thick 5-20mm clean aggregate sub base" },
  { key: "col38", title: "Permeable Geotextile Layer" },
  { key: "col39", title: "150x50mm PCC Edgings" },
  { key: "col40", title: "200x50mm PCC Edgings" },
  { key: "col41", title: "Timber Edgings" },
  { key: "col42", title: "PCC Kerbs" },
  { key: "col43", title: "xxxx Kerb" },
  { key: "col44", title: "xxxx Kerb" },
  { key: "col45", title: "xxxx Kerb" },
  { key: "col46", title: "Key Kerb" },
  { key: "col47", title: "Recessed C&F" },
  // Surface Course - Private Macadam Drive (col48-col52)
  { key: "col48", title: "Remove Timber edgings" },
  { key: "col49", title: "Sweep & Tack" },
  { key: "col50", title: "xxmm DB Surface course" },
  { key: "col51", title: "xxmm HRA Surface course" },
  { key: "col52", title: "xxmm SMA Surface course" },
  // Surface Course - Private Block Paved Drive (col53-col57)
  { key: "col53", title: "Sweep" },
  { key: "col54", title: "Puncture Macadam" },
  { key: "col55", title: "xxmm sand Laying Course" },
  { key: "col56", title: "60mm Concrete paviors" },
  { key: "col57", title: "80mm Concrete paviors" },
  // Surface Course - Private Permeable Paving (col58-col61)
  { key: "col58", title: "Puncture Macadam" },
  { key: "col59", title: "xxmm thick 2-6mm laying Aggregate" },
  { key: "col60", title: "60mm Concrete paviors" },
  { key: "col61", title: "80mm Concrete paviors" },
  // Surface Course - Shared Macadam Drive (col62-col65)
  { key: "col62", title: "Sweep & Tack" },
  { key: "col63", title: "xxmm DB Surface course" },
  { key: "col64", title: "xxmm HRA Surface course" },
  { key: "col65", title: "xxmm SMA Surface course" },
  // Surface Course - Shared Block Paving (col66-col69)
  { key: "col66", title: "Puncture Macadam" },
  { key: "col67", title: "xxmm sand Laying Course" },
  { key: "col68", title: "60mm Concrete paviors" },
  { key: "col69", title: "80mm Concrete paviors" },
  // Surface Course - Shared Permeable Paving (col70-col77)
  { key: "col70", title: "Puncture Macadam" },
  { key: "col71", title: "xxmm thick 2-6mm laying Aggregate" },
  { key: "col72", title: "60mm Concrete paviors" },
  { key: "col73", title: "80mm Concrete paviors" },
  { key: "col74", title: "Adjust Ironwork" },
  { key: "col75", title: "Drive Demarcation" },
  { key: "col76", title: "Edging Remedials" },
  { key: "col77", title: "Kerb Remedials" },
];

interface PlotDrivesRow {
  key: string;
  plotNo: string;
  housetype: string;
  col1: number;
  col2: number;
  col3: number;
  col4: number;
  col5: number;
  col6: number;
  col7: number;
  col8: number;
  col9: number;
  col10: number;
  col11: number;
  col12: number;
  col13: number;
  col14: number;
  col15: number;
  col16: number;
  col17: number;
  col18: number;
  col19: number;
  col20: number;
  col21: number;
  col22: number;
  col23: number;
  col24: number;
  col25: number;
  col26: number;
  col27: number;
  col28: number;
  col29: number;
  col30: number;
  col31: number;
  col32: number;
  col33: number;
  col34: number;
  col35: number;
  col36: number;
  col37: number;
  col38: number;
  col39: number;
  col40: number;
  col41: number;
  col42: number;
  col43: number;
  col44: number;
  col45: number;
  col46: number;
  col47: number;
  col48: number;
  col49: number;
  col50: number;
  col51: number;
  col52: number;
  col53: number;
  col54: number;
  col55: number;
  col56: number;
  col57: number;
  col58: number;
  col59: number;
  col60: number;
  col61: number;
  col62: number;
  col63: number;
  col64: number;
  col65: number;
  col66: number;
  col67: number;
  col68: number;
  col69: number;
  col70: number;
  col71: number;
  col72: number;
  col73: number;
  col74: number;
  col75: number;
  col76: number;
  col77: number;
}

// Function to convert Excel column letter to index (A=0, B=1, ..., Z=25, AA=26, etc.)
const excelColumnToIndex = (column: string): number => {
  let result = 0;
  for (let i = 0; i < column.length; i++) {
    result = result * 26 + (column.charCodeAt(i) - "A".charCodeAt(0) + 1);
  }
  return result - 1;
};

// Excel column mapping based on the hierarchy provided
const EXCEL_COLUMN_MAPPING = {
  // DRIVES TO BINDER - PRIVATE DRIVES - Private Macadam Drive
  "Private Macadam Drive": {
    "Level & Compact": excelColumnToIndex("E"), // Column E = 4
    "Geotextile Layer": excelColumnToIndex("F"), // Column F = 5
    "xxxmm Sub base": excelColumnToIndex("G"), // Column G = 6
    "xxmm DB  Base": excelColumnToIndex("H"), // Column H = 7
    "xxmm DB Binder": excelColumnToIndex("I"), // Column I = 8
    "Sealing Grit": excelColumnToIndex("J"), // Column J = 9
    // DRIVES SURFACE COURSE - PRIVATE DRIVES - Private Macadam Drive
    "Sweep & Tack": excelColumnToIndex("BA"), // Column BA = 56
    "xxmm DB Surface course": excelColumnToIndex("BB"), // Column BB = 57
    "xxmm HRA Surface course": excelColumnToIndex("BC"), // Column BC = 58
    "xxmm SMA Surface course": excelColumnToIndex("BD"), // Column BD = 59
  },

  // DRIVES TO BINDER - PRIVATE DRIVES - Private Block Paved Drive
  "Private Block Paved Drive": {
    "Level & Compact": excelColumnToIndex("K"), // Column K = 10
    "Geotextile Layer": excelColumnToIndex("L"), // Column L = 11
    "300mm Additional/Capping": excelColumnToIndex("M"), // Column M = 12 (mapped to xxxmm Sub base in UI)
    "250mm Sub base": excelColumnToIndex("N"), // Column N = 13 (mapped to xxmm DB Base in UI)
    "100mm DB Base": excelColumnToIndex("O"), // Column O = 14 (mapped to xxmm DB Binder in UI)
    "xxmm DB Binder": excelColumnToIndex("P"), // Column P = 15
    // DRIVES SURFACE COURSE - PRIVATE DRIVES - Private Block Paved Drive
    Sweep: excelColumnToIndex("BE"), // Column BE = 60
    "50mm sand Laying Course": excelColumnToIndex("BF"), // Column BF = 61
    "60mm Concrete paviors": excelColumnToIndex("BG"), // Column BG = 62
    "80mm Concrete paviors": excelColumnToIndex("BH"), // Column BH = 63
  },

  // DRIVES TO BINDER - PRIVATE DRIVES - Permeable Paving
  "Permeable Paving": {
    "Level & Compact": excelColumnToIndex("R"), // Column R = 17
    "Permeable Geotextile Layer": excelColumnToIndex("S"), // Column S = 18
    "xxmm DB binder Running course": excelColumnToIndex("T"), // Column T = 19
    "xxmm 63-10mm Sub Base - Varied Depths": excelColumnToIndex("U"), // Column U = 20
    "xxmm 5-20mm clean aggregate sub base": excelColumnToIndex("V"), // Column V = 21
    "Permeable Geotextile Layer": excelColumnToIndex("W"), // Column W = 22
    // DRIVES SURFACE COURSE - PRIVATE DRIVES - Private Permeable Paving
    "xxmm thick 2-6mm laying Aggregate": excelColumnToIndex("BI"), // Column BI = 64
    "60mm Concrete paviors": excelColumnToIndex("BJ"), // Column BJ = 65
    "80mm Concrete paviors": excelColumnToIndex("BK"), // Column BK = 66
  },
};

const PlotDrives: React.FC = () => {
  // Scroll to top on page load
  useScrollToTop();

  const { state: tender, updateField, saveTender } = useTender();
  const { user } = useAuth();
  const navigate = useNavigate();
  const createBoqMutation = useCreateBillOfQuantities();

  // Checkbox state logic for groundworks totals
  const getCheckboxState = (key: string, fallbackPercent: number): boolean => {
    const saved = localStorage.getItem(key);
    if (saved !== null) {
      return JSON.parse(saved);
    }
    return fallbackPercent > 0;
  };

  const preliminariesPercent = tender.globalSettings?.groundworks?.preliminaries || 0;
  const ohpPercent = tender.globalSettings?.groundworks?.overheadAndProfit || 0;
  const discountPercent = tender.globalSettings?.groundworks?.discount || 0;

  const includePreliminaries = getCheckboxState("groundworks_includePreliminariesDisplay", preliminariesPercent);
  const includeOhp = getCheckboxState("groundworks_includeOhpDisplay", ohpPercent);
  const includeDiscount = getCheckboxState("groundworks_includeDiscountDisplay", discountPercent);

  const groundworksTotals = useGroundworksTotalCalculations({
    includePreliminaries,
    includeOhp,
    includeDiscount,
  });
  const { data: itemLibraryData } = useItemLibrary(tender.region || "");

  const [data, setData] = useState<PlotDrivesRow[]>([]);
  const [driveStage, setDriveStage] = useState<"binder" | "surface">("binder");
  const [activeTab, setActiveTab] = useState<string>("privateMacadam");
  // Defers each drive-type tab's Table by one paint cycle so a tender with a
  // lot of plots doesn't make this step look frozen while a table's
  // render/layout blocks the main thread - see useTableRenderDeferral's own
  // doc comment. Re-keyed on `activeTab`/`driveStage` since switching either
  // unmounts/mounts a brand new Table (`binderTabItems`/`surfaceTabItems`,
  // selected by `driveStage`, each keyed by `activeTab`), not just a
  // dataSource update - so the loader should reappear on every switch, not
  // just the initial mount. Purely a perceived-lag fix: once it flips false
  // the tables render exactly as before.
  const isTableLoading = useTableRenderDeferral([activeTab, driveStage]);
  const [columnHeaders, setColumnHeaders] = useState<Record<string, string>>(
    {}
  );
  const [dimensionValues, setDimensionValues] = useState<
    Record<string, string>
  >({});

  // Merge static col## codes with any evaluate codes stored in dimensionValues (e.g. ALP041 for DB Base)
  const effectiveColCodes = React.useMemo(() => {
    const merged = { ...PLOT_DRIVES_COL_CODES };
    Object.entries(dimensionValues).forEach(([key, val]) => {
      if (/^(ALP|GWALP|ACL|ZEV|HAUL)[A-Z0-9]/i.test(val)) {
        merged[key] = { code: val.toUpperCase() };
      } else {
        const labelCode = LABEL_TO_ALP_CODE_MAP[val];
        if (labelCode) merged[key] = { code: labelCode };
      }
    });
    return merged;
  }, [dimensionValues]);

  // Dev-mode per-column code picks from the templates API (colKey → code).
  // Persisted via groundworksItemLibrary.plotDrives (see plotDrivesCodeOverrides.ts).
  const [codeOverrides, setCodeOverrides] = useState<Record<string, string>>({});
  const codeSelect = React.useMemo<CodeSelectConfig>(
    () => ({
      overrides: codeOverrides,
      disabled: !!tender?.isViewMode,
      onChange: (colKey, code) => {
        setCodeOverrides((prev) => {
          const next = { ...prev };
          if (code) next[colKey] = code.toUpperCase();
          else delete next[colKey];
          return next;
        });
        // Let the context sync effect run first, then write the draft so a reload restores the pick
        setTimeout(() => saveTender(), 300);
      },
    }),
    [codeOverrides, tender?.isViewMode, saveTender],
  );
  const addColumnCodes = <T extends object>(
    columns: Parameters<typeof addColumnCodesBase<T>>[0],
    references: Parameters<typeof addColumnCodesBase<T>>[1],
  ) => addColumnCodesBase(columns, references, undefined, codeSelect);

  // Temporary state for pending header changes (only applied when user clicks Apply)
  const [tempDimensionValues, setTempDimensionValues] = useState<
    Record<string, string>
  >({});
  const [tempColumnHeaders, setTempColumnHeaders] = useState<
    Record<string, string>
  >({});
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);

  // Change detection hook
  /* const {
    hasChanges,
    trackFieldChange,
    trackRowAdded,
    trackRowDeleted,
    resetChanges,
    getChangeSummary,
  } = useChangeDetection<PlotDrivesRow[]>({
    pageId: "plot-drives",
    onChangeDetected: (changes) => {
    },
  }); */

  // Create dynamic excel columns based on imported headers
  const dynamicExcelColumns = React.useMemo(() => {
    const baseExcelColumns = excelColumns.slice(0, 3); // key, plotNo, housetype
    const dynamicCols = excelColumns.slice(3).map((col) => {
      // Use columnHeaders if available, otherwise check dimensionValues for asphalt codes
      let title = col.title;

      if (columnHeaders[col.key]) {
        title = columnHeaders[col.key];
      } else if (
        dimensionValues[col.key] &&
        dimensionValues[col.key].startsWith("ALP")
      ) {
        const asphaltCode = dimensionValues[col.key] as EvaluateCode;
        if (EVALUATE_CODES[asphaltCode]) {
          title = EVALUATE_CODES[asphaltCode].label;
        }
      }

      return {
        key: col.key,
        title: title,
      };
    });
    return [...baseExcelColumns, ...dynamicCols];
  }, [columnHeaders, dimensionValues]);

  const {
    importLoading,
    importModalVisible,
    setImportModalVisible,
    handleExportExcel,
    handleDownloadTemplate,
    handleImportExcel: baseHandleImportExcel,
  } = useExcelImportExport<PlotDrivesRow>({
    filename: "PlotDrives",
    sheetName: "Plot Drives",
    columns: dynamicExcelColumns,
    data: data,
    onImport: (importedData) => {
      const processedData = importedData.map((row, index) => {
        const emptyRow = createEmptyRow(`imported_${Date.now()}_${index}`);
        const merged = { ...emptyRow, ...row };

        // Round all numeric fields to 2 decimals
        Object.keys(merged).forEach((key) => {
          if (typeof merged[key] === "number" && key !== "key") {
            merged[key] = Math.round(merged[key] * 1000) / 1000;
          }
        });

        return merged;
      });
      setData(processedData);
      updateField("groundworks", {
        ...tender.groundworks,
        plotDrivesTable: processedData,
      });
    },
    skipValidation: true,
  });

  // Custom import handler - header-based mapping like Drainage
  const handleImportExcel = async (file: File) => {
    setImportModalVisible(false);

    try {
      const XLSX = await import("xlsx");
      const reader = new FileReader();

      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: "array" });
          const worksheet = workbook.Sheets[workbook.SheetNames[0]];

          const jsonData = XLSX.utils.sheet_to_json(worksheet, {
            header: 1,
            raw: true,
            blankrows: false,
          }) as any[][];

          jsonData.forEach((row, index) => {
          });

          // Use Row 3 (index 3) as the header row
          const headerRowIndex = 3;


          if (jsonData.length <= headerRowIndex) {
            alert(
              "Excel file must have at least 4 rows. Row 3 should contain column headers."
            );
            return;
          }

          const headers = jsonData[headerRowIndex];

          // Plot No and Housetype are BASE COLUMNS - always at index 0 and 1
          const plotNoIdx = 0;
          const housetypeIdx = 1;


          // Row 3 contains headers for DATA COLUMNS starting from column 2 onwards
          // Map Excel columns to col1, col2, col3... based on their actual position
          const extractedHeaders: Record<string, string> = {};
          const columnIndexMap: Record<string, number> = {}; // Maps col1 -> Excel column index

          let colNumber = 1;
          for (let i = 2; i < headers.length && colNumber <= 77; i++) {
            const headerValue = String(headers[i] || "").trim();
            if (headerValue) {
              const colKey = `col${colNumber}`;
              extractedHeaders[colKey] = headerValue;
              columnIndexMap[colKey] = i; // Store the Excel column index for this col
              colNumber++;
            }
          }

          // General section boundary alignment: each section must start with "Level & Compact".
          // Expected positions: col1, col7, col14, col20, col26, col33.
          // If any section's "Level & Compact" lands earlier than expected (due to missing columns
          // in the imported Excel), insert empty slots to realign it to the correct position.
          const expectedSectionStarts = [1, 7, 14, 20, 26, 33];
          const actualSectionStarts = Object.keys(extractedHeaders)
            .filter((key) => {
              const h = (extractedHeaders[key] || "").toLowerCase();
              return h.includes("level") && h.includes("compact");
            })
            .map((key) => parseInt(key.replace("col", ""), 10))
            .sort((a, b) => a - b)
            .slice(0, 6);

          for (let i = 0; i < actualSectionStarts.length; i++) {
            const actual = actualSectionStarts[i];
            const expected = expectedSectionStarts[i];
            if (actual < expected) {
              const slotsToInsert = expected - actual;
              for (let s = 0; s < slotsToInsert; s++) {
                for (let colNum = 77; colNum > actual; colNum--) {
                  const destKey = `col${colNum}`;
                  const srcKey = `col${colNum - 1}`;
                  if (columnIndexMap[srcKey] !== undefined) {
                    columnIndexMap[destKey] = columnIndexMap[srcKey];
                    extractedHeaders[destKey] = extractedHeaders[srcKey];
                  } else {
                    delete columnIndexMap[destKey];
                    delete extractedHeaders[destKey];
                  }
                }
                delete columnIndexMap[`col${actual}`];
                delete extractedHeaders[`col${actual}`];
                for (let j = i; j < actualSectionStarts.length; j++) {
                  actualSectionStarts[j]++;
                }
              }
            }
          }

          // Migrate old labels on import
          const IMPORT_LABEL_MIGRATIONS: Record<string, string> = {
            'Permeable geotextile membrane': 'Permeable Geotextile Layer',
            'Edgings 150 x 50': '150x50mm PCC Edgings',
            'Edgings 200 x 50': '200x50mm PCC Edgings',
            'Timber Edging': 'Timber Edgings',
            'Kerbs': 'PCC Kerbs',
            'Formpave BN key kerb': 'Key Kerb',
            'Recessed covers & frames in block paved areas': 'Recessed C&F',
          };
          Object.keys(extractedHeaders).forEach((key) => {
            const migrated = IMPORT_LABEL_MIGRATIONS[extractedHeaders[key]];
            if (migrated) extractedHeaders[key] = migrated;
          });

          // Validate DB Base and DB Binder columns — only keep headers that match
          // a valid dropdown option label. If the Excel has an abbreviated value like
          // "60mm DB Binder" that doesn't appear in the configure modal dropdown,
          // remove it so the column stays at its unconfigured placeholder.
          const validDbBaseLabels = new Set(
            Object.values(EVALUATE_CODES)
              .filter((l) => ["ALP041", "ALP042"].includes(l.code))
              .map((l) => l.label.toLowerCase())
          );
          const validDbBinderLabels = new Set(
            Object.values(EVALUATE_CODES)
              .filter((l) => ["ALP043", "ALP044", "ALP045", "ALP046", "ALP047"].includes(l.code))
              .map((l) => l.label.toLowerCase())
          );
          const dbBaseColKeys = new Set(["col4", "col10", "col23", "col29"]);
          const dbBinderColKeys = new Set(["col5", "col11", "col24", "col30"]);

          Object.keys(extractedHeaders).forEach((key) => {
            const h = (extractedHeaders[key] || "").toLowerCase().trim();
            if (dbBaseColKeys.has(key) && !validDbBaseLabels.has(h)) {
              delete extractedHeaders[key];
            } else if (dbBinderColKeys.has(key) && !validDbBinderLabels.has(h)) {
              delete extractedHeaders[key];
            }
          });

          setColumnHeaders(extractedHeaders);

          // Extract dimension values from imported headers
          const extractedDimensionValues: Record<string, string> = {};

          // Columns for Sub base, DB Base, DB Binder, Surface Course - use full header as-is (no dimension extraction)
          const useHeaderAsIsColumns = [
            "col3",
            "col9",
            "col10",
            "col22",
            "col28", // Sub base columns
            "col4",
            "col11",
            "col23",
            "col29", // DB Base columns
            "col5",
            "col12",
            "col24",
            "col30", // DB Binder columns
            "col50", "col63",   // DB Surface course (Private/Shared Macadam)
            "col51", "col52",   // Private Macadam HRA/SMA Surface course
            "col55", "col67",   // sand Laying Course (Private/Shared Block Paved)
            "col59", "col71",   // 2-6mm laying Aggregate (Private/Shared Permeable)
            "col60", "col61",   // Private Permeable Paving 60mm/80mm Concrete paviors
            "col72", "col73",   // Shared Permeable Paving 60mm/80mm Concrete paviors
            "col56", "col57",   // Concrete paviors (Private Block Paved)
            "col64", "col65",   // Shared Macadam HRA/SMA Surface course
            "col68", "col69",   // Concrete paviors (Shared Block Paved)
          ];

          // DB binder Running course dropdown options for matching
          const binderRunningCourseOptions = [
            "50mm thick AC 20mm dense bin",
            "60mm thick AC 20mm dense bin",
            "75mm thick AC 20mm dense bin",
            "80mm thick AC 20mm dense bin",
            "100mm thick AC 20mm dense bin",
          ];

          // Columns that need special extraction (leading dimension before fixed text)
          const specialExtractionColumns: Record<string, { fixedText: string; type: "dimension" | "dropdown"; options?: string[] }> = {
            col16: { fixedText: "DB binder Running course", type: "dropdown", options: binderRunningCourseOptions },
            col35: { fixedText: "DB binder Running course", type: "dropdown", options: binderRunningCourseOptions },
            col17: { fixedText: "thick 63-10mm Sub Base - Varied Depths", type: "dimension" },
            col36: { fixedText: "thick 63-10mm Sub Base - Varied Depths", type: "dimension" },
            col18: { fixedText: "thick 5-20mm clean aggregate sub base", type: "dimension" },
            col37: { fixedText: "thick 5-20mm clean aggregate sub base", type: "dimension" },
          };

          Object.keys(extractedHeaders).forEach((colKey) => {
            const importedHeader = String(
              extractedHeaders[colKey] || ""
            ).trim();
            // Find the default header for this column
            const defaultCol = excelColumns.find((col) => col.key === colKey);


            // For Sub base, DB Base, DB Binder columns - use full header as-is (no extraction)
            if (useHeaderAsIsColumns.includes(colKey)) {
              if (importedHeader) {
                // DB Base/Binder: only store if the label matches a valid configure-modal option
                if (dbBaseColKeys.has(colKey)) {
                  if (validDbBaseLabels.has(importedHeader.toLowerCase())) {
                    extractedDimensionValues[colKey] = importedHeader;
                  }
                } else if (dbBinderColKeys.has(colKey)) {
                  if (validDbBinderLabels.has(importedHeader.toLowerCase())) {
                    extractedDimensionValues[colKey] = importedHeader;
                  }
                } else {
                  extractedDimensionValues[colKey] = importedHeader;
                }
              }
              return;
            }

            // Special extraction for columns with fixed text after the dynamic dimension
            const specialConfig = specialExtractionColumns[colKey];
            if (specialConfig) {
              if (specialConfig.type === "dropdown" && specialConfig.options) {
                // Match against dropdown options (e.g., binder running course)
                const matchedOption = specialConfig.options.find(
                  (opt) => importedHeader.toLowerCase() === opt.toLowerCase()
                );
                if (matchedOption) {
                  extractedDimensionValues[colKey] = matchedOption;
                }
              } else {
                // Extract leading dimension before the fixed text
                // e.g., "200mm 5-20mm clean aggregate sub base" → extract "200"
                // e.g., "63-10mm Sub Base - Varied Depths" → no leading dimension, skip
                const fixedTextIndex = importedHeader.toLowerCase().indexOf(specialConfig.fixedText.toLowerCase());
                if (fixedTextIndex > 0) {
                  // There's text before the fixed portion — extract the leading dimension
                  const leadingText = importedHeader.substring(0, fixedTextIndex).trim();
                  const dimMatch = leadingText.match(/^(\d+(?:\.\d+)?)/);
                  if (dimMatch) {
                    extractedDimensionValues[colKey] = dimMatch[1];
                  }
                }
                // If fixedTextIndex === 0 or -1, no leading dimension to extract
              }
              return;
            }

            if (defaultCol && /\b(x{2,}(?:mm)?)\b/gi.test(defaultCol.title)) {
              // This column has a dimension pattern in the default title
              // Extract just the number from imported header
              // Examples: "900" from "900mm Sub base", "150x200" from "150x200 Kerb", "100" from "100mm DB Base"
              const dimensionMatch = importedHeader.match(/^(\d+(?:\.\d+)?(?:x\d+(?:\.\d+)?)?)/i);
              if (dimensionMatch) {
                const extractedValue = dimensionMatch[1];
                // Store just the number/dimension (e.g., "900" or "150x200")
                // mm will be added automatically by getColumnTitle when displaying
                extractedDimensionValues[colKey] = extractedValue;
              }
            }
          });


          // Set dimension values - this will populate the input fields
          setDimensionValues(extractedDimensionValues);

          // Data starts from Row 4 (index 4) - right after Row 3 (index 3) headers
          const allDataRows = jsonData.slice(headerRowIndex + 1);

          // Filter out empty rows and rows with "total" or "sum"
          const dataRows = allDataRows.filter((row) => {
            if (!row || row.length === 0) return false;

            const plotNoValue = String(row[0] || "")
              .trim()
              .toLowerCase();
            const houseTypeValue = String(row[1] || "")
              .trim()
              .toLowerCase();

            // Skip rows that are totals, sums, or completely empty
            if (
              plotNoValue.includes("total") ||
              plotNoValue.includes("sum") ||
              houseTypeValue.includes("total") ||
              houseTypeValue.includes("sum") ||
              (plotNoValue === "" && houseTypeValue === "")
            ) {
              return false;
            }

            return true;
          });



          const allImportedRows: PlotDrivesRow[] = dataRows
            .filter(
              (row) =>
                row &&
                row.length > 0 &&
                row.some(
                  (cell) => cell !== null && cell !== undefined && cell !== ""
                )
            )
            .map((row, index) => {
              const rowData: any = {
                key: `imported_${Date.now()}_${index}`,
                plotNo: String(row[plotNoIdx] || ""),
                housetype: String(row[housetypeIdx] || ""),
              };

              if (index === 0) {
              }

              // Map all 77 columns using the columnIndexMap
              for (let colNum = 1; colNum <= 77; colNum++) {
                const colKey = `col${colNum}`;
                const excelColIndex = columnIndexMap[colKey];
                if (excelColIndex !== undefined) {
                  const value =
                    Math.round((Number(row[excelColIndex]) || 0) * 1000) / 1000;
                  rowData[colKey] = value;

                  if (index === 0 && colNum <= 5) {
                  }
                } else {
                  rowData[colKey] = 0;
                  if (index === 0 && colNum <= 5) {
                  }
                }
              }

              return rowData as PlotDrivesRow;
            })
            .filter((row) => {
              // Additional filter - keep rows with valid plot numbers and house types
              const plotNoStr = String(row.plotNo || "").trim();
              const hasPlotNo =
                plotNoStr !== "" &&
                plotNoStr !== "0" &&
                plotNoStr !== "null" &&
                plotNoStr !== "undefined";
              const housetypeStr = String(row.housetype || "").trim();
              const hasValidHouseType =
                housetypeStr !== "" &&
                !housetypeStr.toLowerCase().includes("total") &&
                !housetypeStr.toLowerCase().includes("sum") &&
                isNaN(Number(housetypeStr)) && // Not purely numeric
                housetypeStr.match(/[a-zA-Z]/); // Contains letters
              return hasPlotNo && hasValidHouseType;
            });


          if (allImportedRows.length === 0) {
            alert("No valid data found to import. Check console for details.");
            return;
          }


          try {
            setData(allImportedRows);
            updateField("groundworks", {
              ...tender.groundworks,
              plotDrivesTable: allImportedRows,
              plotDrivesColumnHeaders: extractedHeaders,
              plotDrivesDimensionValues: extractedDimensionValues,
            });
            alert(
              `Successfully imported ${allImportedRows.length} rows with ${
                Object.keys(extractedHeaders).length
              } column headers from Row 3`
            );
          } catch (saveError: any) {
            console.error("Error saving data:", saveError);
            alert(
              `Failed to save imported data: ${
                saveError?.message || "Unknown error"
              }`
            );
          }
        } catch (error: any) {
          console.error("=== IMPORT ERROR ===");
          console.error("Error object:", error);
          console.error("Error message:", error?.message);
          console.error("Error stack:", error?.stack);
          alert(
            `Import failed: ${
              error?.message || "Unknown error"
            }. Check console for details.`
          );
        }
      };

      reader.readAsArrayBuffer(file);
    } catch (error: any) {
      console.error("Import error:", error);
      alert(`Import failed: ${error?.message || "Unknown error"}`);
    }
  };

  useEffect(() => {
    if (tender?.groundworks?.plotDrivesTable) {
      setData(tender.groundworks.plotDrivesTable);
    } else {
      // Initialize with empty row
      setData([createEmptyRow()]);
    }
  }, [tender?.groundworks?.plotDrivesTable]);

  // Load column headers and dimension values from tender context on mount
  useEffect(() => {
    // Skip if already loaded
    if (isInitialLoadComplete) return;

    // Wait for tender data to be available
    if (!tender?.groundworks) return;

    let loadedHeaders: Record<string, string> = {};
    let loadedDimensions: Record<string, string> = {};

    // Load dimension values first
    if (tender?.groundworks?.plotDrivesDimensionValues) {
      loadedDimensions = tender.groundworks.plotDrivesDimensionValues;
      setDimensionValues(loadedDimensions);
    }

    if (tender?.groundworks?.plotDrivesCodeOverrides) {
      setCodeOverrides(tender.groundworks.plotDrivesCodeOverrides);
    }

    // Load column headers
    if (tender?.groundworks?.plotDrivesColumnHeaders) {
      const headers = tender.groundworks.plotDrivesColumnHeaders;
      loadedHeaders = { ...headers };
      // Migrate old labels → new labels for tenders saved before renames
      const LABEL_MIGRATIONS: Record<string, string> = {
        'Permeable geotextile membrane': 'Permeable Geotextile Layer',
        'Edgings 150 x 50': '150x50mm PCC Edgings',
        'Edgings 200 x 50': '200x50mm PCC Edgings',
        'Timber Edging': 'Timber Edgings',
        'Kerbs': 'PCC Kerbs',
        'Formpave BN key kerb': 'Key Kerb',
        'Recessed covers & frames in block paved areas': 'Recessed C&F',
      };
      Object.keys(loadedHeaders).forEach((key) => {
        const migrated = LABEL_MIGRATIONS[loadedHeaders[key]];
        if (migrated) loadedHeaders[key] = migrated;
      });

      // Strip any DB Base / DB Binder headers that don't match a valid configure-modal
      // dropdown option (e.g. "90mm DB Base" saved by an older import). These must be
      // cleared from both headers and dimensions so the column reverts to its placeholder.
      {
        const validDbBase = new Set(
          Object.values(EVALUATE_CODES)
            .filter((l) => ["ALP041", "ALP042"].includes(l.code))
            .map((l) => l.label.toLowerCase())
        );
        const validDbBinder = new Set(
          Object.values(EVALUATE_CODES)
            .filter((l) => ["ALP043", "ALP044", "ALP045", "ALP046", "ALP047"].includes(l.code))
            .map((l) => l.label.toLowerCase())
        );
        const dbBaseCols = new Set(["col4", "col10", "col23", "col29"]);
        const dbBinderCols = new Set(["col5", "col11", "col24", "col30"]);

        [...dbBaseCols, ...dbBinderCols].forEach((key) => {
          const validSet = dbBaseCols.has(key) ? validDbBase : validDbBinder;
          const h = (loadedHeaders[key] || "").toLowerCase().trim();
          if (h && !validSet.has(h)) {
            delete loadedHeaders[key];
            delete loadedDimensions[key];
          }
          const d = (loadedDimensions[key] || "").toLowerCase().trim();
          if (d && !validSet.has(d)) {
            delete loadedDimensions[key];
          }
        });
      }
    }

    // Apply section boundary alignment fix on load — same logic as import.
    // Handles data saved before the import-time alignment fix was in place.
    {
      const expectedSectionStarts = [1, 7, 14, 20, 26, 33];
      const actualSectionStarts = Object.keys(loadedHeaders)
        .filter((key) => {
          const h = (loadedHeaders[key] || "").toLowerCase();
          return h.includes("level") && h.includes("compact");
        })
        .map((key) => parseInt(key.replace("col", ""), 10))
        .sort((a, b) => a - b)
        .slice(0, 6);

      let dataToAlign: any[] | null =
        tender?.groundworks?.plotDrivesTable
          ? tender.groundworks.plotDrivesTable.map((row: any) => ({ ...row }))
          : null;
      let headersAligned = false;

      for (let i = 0; i < actualSectionStarts.length; i++) {
        const actual = actualSectionStarts[i];
        const expected = expectedSectionStarts[i];
        if (actual < expected) {
          headersAligned = true;
          const slotsToInsert = expected - actual;
          for (let s = 0; s < slotsToInsert; s++) {
            // Shift headers right
            for (let colNum = 77; colNum > actual; colNum--) {
              const destKey = `col${colNum}`;
              const srcKey = `col${colNum - 1}`;
              if (loadedHeaders[srcKey]) {
                loadedHeaders[destKey] = loadedHeaders[srcKey];
              } else {
                delete loadedHeaders[destKey];
              }
            }
            delete loadedHeaders[`col${actual}`];

            // Shift data rows right
            if (dataToAlign) {
              dataToAlign = dataToAlign.map((row: any) => {
                const newRow = { ...row };
                for (let colNum = 77; colNum > actual; colNum--) {
                  newRow[`col${colNum}`] = row[`col${colNum - 1}`] ?? 0;
                }
                newRow[`col${actual}`] = 0;
                return newRow;
              });
            }

            for (let j = i; j < actualSectionStarts.length; j++) {
              actualSectionStarts[j]++;
            }
          }
        }
      }

      // Persist the aligned data so subsequent reloads are already correct
      if (headersAligned && dataToAlign) {
        setData(dataToAlign);
        updateField("groundworks", {
          ...(tender?.groundworks || {}),
          plotDrivesTable: dataToAlign,
          plotDrivesColumnHeaders: loadedHeaders,
        });
      }
    }

    // Reconstruct missing headers from dimension values (for asphalt codes and GWALPD codes)
    Object.entries(loadedDimensions).forEach(([key, value]) => {
      if (
        value &&
        typeof value === "string" &&
        (value.startsWith("ALP") || value.startsWith("GWALPD"))
      ) {
        const evaluateCode = value as EvaluateCode;
        if (EVALUATE_CODES[evaluateCode]) {
          // Only set if not already in loaded headers
          if (!loadedHeaders[key]) {
            loadedHeaders[key] = EVALUATE_CODES[evaluateCode].label;
          }
        }
      }
    });

    // Reconstruct headers for simple string values
    // With new surface layout all concrete paviors are fixed (60mm/80mm) so no reconstruction needed
    const simpleValueColumns: string[] = [];
    simpleValueColumns.forEach((colKey) => {
      const value = loadedDimensions[colKey];
      if (value && typeof value === "string" && !loadedHeaders[colKey]) {
        loadedHeaders[colKey] = value;
      }
    });

    // Reconstruct headers for Sub base columns (col3, col10, col16, col22, col28, col35)
    // These store just the number value (e.g., "150") and need to be formatted as "150mm thick MOT Type 1" or "150mm DB binder Running course"
    const subBaseColumns = [
      "col3",
      "col9",
      "col22",
      "col28",
    ];
    subBaseColumns.forEach((colKey) => {
      const value = loadedDimensions[colKey];
      if (value && typeof value === "string" && !loadedHeaders[colKey]) {
        const formattedValue = value.includes("mm") ? value : `${value}mm`;
        const formattedHeader = value.toLowerCase().includes("thick mot type 1")
          ? value
          : `${formattedValue} thick MOT Type 1`;
        loadedHeaders[colKey] = formattedHeader;
      }
    });

    // Reconstruct headers for DB binder Running course columns (col16, col35)
    // These now store the full selected value (e.g., "50mm thick AC 20mm dense bin")
    ["col16", "col35"].forEach((colKey) => {
      const value = loadedDimensions[colKey];
      if (value && typeof value === "string" && !loadedHeaders[colKey]) {
        loadedHeaders[colKey] = value;
      }
    });

    // Reconstruct headers for aggregate sub base columns (col17, col36)
    // Clean up legacy dimension values that were incorrectly extracted from old title format
    ["col17", "col36"].forEach((colKey) => {
      const value = loadedDimensions[colKey];
      if (value && typeof value === "string") {
        // Legacy cleanup: if stored header contains old format "Sub base" or "clean aggregate",
        // or dimension value is a plain number with no valid header set, clear it
        const header = loadedHeaders[colKey] || "";
        const isLegacyHeader = header.toLowerCase().includes("sub base") && !header.toLowerCase().includes("varied depths");
        const isLegacyValue = /^\d+(\.\d+)?$/.test(value) && !header.toLowerCase().includes("varied depths");
        if (isLegacyHeader || isLegacyValue) {
          delete loadedDimensions[colKey];
          delete loadedHeaders[colKey];
          return;
        }
        // Valid value — reconstruct header if missing
        if (!loadedHeaders[colKey]) {
          const formattedValue = value.includes("mm") ? value : `${value}mm`;
          const formattedHeader = value.toLowerCase().includes("varied depths")
            ? value
            : `${formattedValue} thick 63-10mm Sub Base - Varied Depths`;
          loadedHeaders[colKey] = formattedHeader;
        }
      }
    });

    // Reconstruct headers for 5-20mm aggregate sub base columns (col18, col37)
    // Clean up legacy values (these were previously select dropdowns storing ALP codes)
    ["col18", "col37"].forEach((colKey) => {
      const value = loadedDimensions[colKey];
      if (value && typeof value === "string") {
        // Legacy cleanup: clear ALP codes or old format values
        if (value.startsWith("ALP") || value.startsWith("GWALPD")) {
          delete loadedDimensions[colKey];
          delete loadedHeaders[colKey];
          return;
        }
        if (!loadedHeaders[colKey]) {
          const formattedValue = value.includes("mm") ? value : `${value}mm`;
          const formattedHeader = value.toLowerCase().includes("5-20mm")
            ? value
            : `${formattedValue} thick 5-20mm clean aggregate sub base`;
          loadedHeaders[colKey] = formattedHeader;
        }
      }
    });

    // Reconstruct headers for DB Base and DB Binder columns
    // These columns store labels directly (e.g., "80mm DB Base", "60mm DB Binder")
    const dbBaseAndBinderColumns = [
      "col4",
      "col11",
      "col23",
      "col29", // DB Base columns
      "col5",
      "col12",
      "col24",
      "col30", // DB Binder columns
    ];
    dbBaseAndBinderColumns.forEach((colKey) => {
      const value = loadedDimensions[colKey];
      if (value && typeof value === "string" && !loadedHeaders[colKey]) {
        loadedHeaders[colKey] = value;
      }
    });

    // Reconstruct headers for Surface course and block paviors columns (evaluate codes or full labels)
    const surfaceCourseColumns = [
      "col50", "col63", // DB Surface course (Private/Shared Macadam)
      "col51", "col52", // Private Macadam HRA/SMA
      "col64", "col65", // Shared Macadam HRA/SMA
      "col56", "col57", // Private Block Paved 60mm/80mm Concrete paviors
      "col68", "col69", // Shared Block Paved 60mm/80mm Concrete paviors
      "col60", "col61", // Private Permeable Paving 60mm/80mm Concrete paviors
      "col72", "col73", // Shared Permeable Paving 60mm/80mm Concrete paviors
    ];
    surfaceCourseColumns.forEach((colKey) => {
      const value = loadedDimensions[colKey];
      if (value && typeof value === "string" && !loadedHeaders[colKey]) {
        // If value is an evaluate code, resolve to label
        if ((value.startsWith("ALP") || value.startsWith("GWALPD")) && EVALUATE_CODES[value as EvaluateCode]) {
          loadedHeaders[colKey] = EVALUATE_CODES[value as EvaluateCode].label;
        } else {
          loadedHeaders[colKey] = value;
        }
      }
    });

    // Reconstruct headers for laying aggregate columns (col59, col71)
    ["col59", "col71"].forEach((colKey) => {
      const existing = loadedHeaders[colKey];
      const value = loadedDimensions[colKey];
      if (!existing && value && typeof value === "string") {
        if (value.toLowerCase().includes("2-6mm laying aggregate")) {
          // Value is already a full header (stored via useHeaderAsIsColumns on import)
          loadedHeaders[colKey] = value;
        } else {
          const formattedValue = value.includes("mm") ? value : `${value}mm`;
          loadedHeaders[colKey] = `${formattedValue} thick 2-6mm laying Aggregate`;
        }
      }
    });

    // Reconstruct headers for sand laying course columns (simple text values)
    ["col55", "col67"].forEach((colKey) => {
      const value = loadedDimensions[colKey];
      if (value && typeof value === "string" && !loadedHeaders[colKey]) {
        loadedHeaders[colKey] = value;
      }
    });

    // Reconstruct headers for kerb columns (col43, col44, col45)
    ["col43", "col44", "col45"].forEach((colKey) => {
      const value = loadedDimensions[colKey];
      if (value && typeof value === "string" && !loadedHeaders[colKey]) {
        // Skip placeholder values (all x's — unconfigured)
        if (/^x+$/i.test(value.trim())) {
          delete loadedDimensions[colKey];
          return;
        }
        loadedHeaders[colKey] = value.toLowerCase().includes("kerb")
          ? value
          : `${value} Kerb`;
      }
    });

    setColumnHeaders(loadedHeaders);
    // Re-set dimension values after any cleanup during reconstruction
    setDimensionValues({ ...loadedDimensions });

    // Mark initial load as complete
    setIsInitialLoadComplete(true);
  }, [tender?.groundworks, isInitialLoadComplete]);

  // Sync column headers and dimension values to tender context whenever they change (no auto-save; save is triggered explicitly in handleConfigApply)
  useEffect(() => {
    if (isInitialLoadComplete) {
      const currentGroundworks = groundworksRef.current || {};
      updateField("groundworks", {
        ...currentGroundworks,
        plotDrivesColumnHeaders: columnHeaders,
        plotDrivesDimensionValues: dimensionValues,
        plotDrivesCodeOverrides: codeOverrides,
      });
    }
  }, [columnHeaders, dimensionValues, codeOverrides, isInitialLoadComplete]);

  // Apply configure-headers modal: save temp values directly to context + API
  const handleConfigApply = () => {
    setDimensionValues(tempDimensionValues);
    setColumnHeaders(tempColumnHeaders);

    const currentGroundworks = groundworksRef.current || {};
    updateField("groundworks", {
      ...currentGroundworks,
      plotDrivesColumnHeaders: tempColumnHeaders,
      plotDrivesDimensionValues: tempDimensionValues,
    });
    setTimeout(() => saveTender(), 100);
  };

  // Auto-sync columnHeaders from dimensionValues when they contain evaluate codes (ALP or GWALPD) or simple values
  useEffect(() => {
    // Skip if not yet loaded (but allow running once after initial load completes)
    if (!isInitialLoadComplete && Object.keys(dimensionValues).length === 0)
      return;

    let needsUpdate = false;
    let dimensionNeedsUpdate = false;
    const updatedHeaders = { ...columnHeaders };
    const updatedDimensionValues = { ...dimensionValues };

    // Simple value columns that store paving type names directly
    // With new surface layout, all concrete paviors are fixed sizes - no reconstruction needed
    const simpleValueColumns: string[] = [];

    // Sub base columns that need "mm thick MOT Type 1" formatting
    const subBaseColumns = [
      "col3",
      "col9",
      "col22",
      "col28",
    ];

    // DB binder Running course columns that store full selected value
    const binderRunningCourseColumns = ["col16", "col35"];

    // Aggregate sub base columns that need "mm 63-10mm Sub Base - Varied Depths" formatting
    const aggregateSubBaseColumns = ["col17", "col36"];

    // 5-20mm aggregate sub base columns
    const aggregate520SubBaseColumns = ["col18", "col37"];

    // DB Base and DB Binder columns that store labels directly
    const dbBaseAndBinderColumns = [
      "col4",
      "col11",
      "col23",
      "col29", // DB Base columns
      "col5",
      "col12",
      "col24",
      "col30", // DB Binder columns
    ];

    // Surface course and block paviors columns that use evaluate codes
    const surfaceCourseColumns = [
      "col50", "col63", // DB Surface course (Private/Shared Macadam)
      "col51", "col52", // Private Macadam HRA/SMA
      "col64", "col65", // Shared Macadam HRA/SMA
      "col56", "col57", // Private Block Paved 60mm/80mm Concrete paviors
      "col68", "col69", // Shared Block Paved 60mm/80mm Concrete paviors
      "col60", "col61", // Private Permeable Paving 60mm/80mm Concrete paviors
      "col72", "col73", // Shared Permeable Paving 60mm/80mm Concrete paviors
    ];

    // Sand laying course columns that store simple text values
    const sandLayingCourseColumns = [
      "col55", "col67", // Private/Shared Block Paved sand Laying Course
    ];

    // Laying aggregate columns that store dimension values
    const layingAggregateColumns = [
      "col59", "col71", // Private/Shared Permeable Paving 2-6mm laying Aggregate
    ];

    // Kerb columns that store text values appended with " Kerb"
    const kerbColumns = [
      "col43", "col44", "col45",
    ];

    // Helper to match imported surface course header to evaluate code
    const matchSurfaceCourseToEvaluateCode = (
      importedHeader: string,
      colKey: string
    ): string | null => {
      const header = importedHeader.toLowerCase();
      // Extract dimension from header (e.g., "20" from "20mm DB Surface course")
      const dimMatch = importedHeader.match(/^(\d+(?:\.\d+)?)/);
      const dimension = dimMatch ? parseFloat(dimMatch[1]) : null;

      // DB Surface columns: ALP048, ALP049, ALP051 (matched by thickness)
      if (["col50", "col63"].includes(colKey)) {
        if (dimension === 20) return "ALP048";
        if (dimension === 25) return "ALP049";
        if (dimension === 30) return "ALP051";
      }
      // HRA Surface columns: all thicknesses/aggregates map to ALP053
      if (["col51", "col64"].includes(colKey)) {
        if (dimension === 20 || dimension === 25 || dimension === 30) return "ALP053";
      }
      // SMA Surface columns: ALP052
      if (["col52", "col65"].includes(colKey)) {
        if (dimension === 25 || dimension === 30) return "ALP052";
      }
      // 60mm Block Paviors columns: match by brand keywords only
      if (["col56", "col68"].includes(colKey)) {
        if (header.includes("charcon") || header.includes("europa")) return "GWALPD002";
        if (header.includes("keyblock")) return "GWALPD010";
        if (header.includes("tegula")) return "GWALPD006";
      }
      // 80mm Block Paviors columns: match by brand keywords only
      if (["col57", "col69"].includes(colKey)) {
        if (header.includes("charcon") || header.includes("europa")) return "GWALPD001";
        if (header.includes("keyblock")) return "GWALPD009";
        if (header.includes("tegula")) return "GWALPD005";
      }
      // 60mm Permeable Paviors columns: match by brand keywords only
      if (["col60", "col72"].includes(colKey)) {
        if (header.includes("charcon") || header.includes("infilta")) return "GWALPD004";
        if (header.includes("marshalls") || header.includes("priora")) return "GWALPD008";
      }
      // 80mm Permeable Paviors columns: match by brand keywords only
      if (["col61", "col73"].includes(colKey)) {
        if (header.includes("charcon") || header.includes("infilta")) return "GWALPD003";
        if (header.includes("marshalls") || header.includes("priora")) return "GWALPD007";
      }
      return null;
    };

    // Surface course codes that have multiple display-variant labels mapping
    // to the same EvaluateCode (DB/HRA/SMA). For these, an existing header
    // (the user's chosen variant) must be preserved instead of being
    // overwritten with the canonical EVALUATE_CODES label.
    const surfaceVariantCodes = ["ALP048", "ALP049", "ALP051", "ALP052", "ALP053"];

    Object.entries(dimensionValues).forEach(([key, value]) => {
      if (value && typeof value === "string") {
        if (value.startsWith("ALP") || value.startsWith("GWALPD")) {
          // Handle evaluate codes
          const evaluateCode = value as EvaluateCode;
          if (EVALUATE_CODES[evaluateCode]) {
            const canonicalLabel = EVALUATE_CODES[evaluateCode].label;
            // Preserve a user-chosen variant label for DB/HRA/SMA surface
            // codes; fall back to the canonical label if the header is empty
            // or for any other evaluate code.
            const expectedLabel =
              surfaceVariantCodes.includes(evaluateCode) && updatedHeaders[key]
                ? updatedHeaders[key]
                : canonicalLabel;
            if (updatedHeaders[key] !== expectedLabel) {
              updatedHeaders[key] = expectedLabel;
              needsUpdate = true;
            }
          }
        } else if (surfaceCourseColumns.includes(key)) {
          // Handle surface course columns - try to match imported header to evaluate code
          const matchedCode = matchSurfaceCourseToEvaluateCode(value, key);
          if (matchedCode) {
            const evaluateCode = matchedCode as EvaluateCode;
            // For DB/HRA/SMA surface codes, preserve the takeoff variant
            // label (e.g. "25mm thick HRA 6mm") instead of overwriting with
            // the canonical EVALUATE_CODES label ("25 or 30mm thick 6 or
            // 10mm HRA"). The configure modal's HRA/SMA selects bind to the
            // header string and only pre-select if the value matches one of
            // the 4 variant labels — the canonical label isn't in that set.
            const expectedLabel = surfaceVariantCodes.includes(evaluateCode)
              ? value
              : (EVALUATE_CODES[evaluateCode]?.label || value);
            // Update dimensionValues to store the evaluate code
            updatedDimensionValues[key] = matchedCode;
            dimensionNeedsUpdate = true;
            if (updatedHeaders[key] !== expectedLabel) {
              updatedHeaders[key] = expectedLabel;
              needsUpdate = true;
            }
          } else {
            // Value is already a direct label or unrecognized - use as-is
            if (updatedHeaders[key] !== value) {
              updatedHeaders[key] = value;
              needsUpdate = true;
            }
          }
        } else if (subBaseColumns.includes(key)) {
          // Handle Sub base columns - format as "thick MOT Type 1"
          const formattedValue = value.includes("mm") ? value : `${value}mm`;
          const expectedHeader = value.toLowerCase().includes("thick mot type 1")
            ? value
            : `${formattedValue} thick MOT Type 1`;

          if (updatedHeaders[key] !== expectedHeader) {
            updatedHeaders[key] = expectedHeader;
            needsUpdate = true;
          }
        } else if (binderRunningCourseColumns.includes(key)) {
          // Handle DB binder Running course columns - value IS the header
          if (updatedHeaders[key] !== value) {
            updatedHeaders[key] = value;
            needsUpdate = true;
          }
        } else if (aggregateSubBaseColumns.includes(key)) {
          // Handle aggregate sub base columns
          const formattedValue = value.includes("mm") ? value : `${value}mm`;
          const expectedHeader = value.toLowerCase().includes("varied depths")
            ? value
            : `${formattedValue} thick 63-10mm Sub Base - Varied Depths`;

          if (updatedHeaders[key] !== expectedHeader) {
            updatedHeaders[key] = expectedHeader;
            needsUpdate = true;
          }
        } else if (aggregate520SubBaseColumns.includes(key)) {
          // Handle 5-20mm aggregate sub base columns
          const formattedValue = value.includes("mm") ? value : `${value}mm`;
          const expectedHeader = value.toLowerCase().includes("5-20mm")
            ? value
            : `${formattedValue} thick 5-20mm clean aggregate sub base`;

          if (updatedHeaders[key] !== expectedHeader) {
            updatedHeaders[key] = expectedHeader;
            needsUpdate = true;
          }
        } else if (layingAggregateColumns.includes(key)) {
          // Handle 2-6mm laying Aggregate columns — reconstruct header from stored value
          const hasLayingAgg = value.toLowerCase().includes("2-6mm laying aggregate");
          const hasThick = value.toLowerCase().includes("thick");
          let expectedHeader: string;
          if (hasLayingAgg && hasThick) {
            expectedHeader = value; // already a full header (e.g. imported as-is)
          } else if (hasLayingAgg && !hasThick) {
            const dimMatch = value.match(/^(\d+(?:\.\d+)?)\s*mm/i);
            const dim = dimMatch ? `${dimMatch[1]}mm` : (value.includes("mm") ? value.split(/\s/)[0] : `${value}mm`);
            expectedHeader = `${dim} thick 2-6mm laying Aggregate`;
          } else {
            const formattedValue = value.includes("mm") ? value : `${value}mm`;
            expectedHeader = `${formattedValue} thick 2-6mm laying Aggregate`;
          }
          if (updatedHeaders[key] !== expectedHeader) {
            updatedHeaders[key] = expectedHeader;
            needsUpdate = true;
          }
        } else if (kerbColumns.includes(key)) {
          // If value is a placeholder (all x's, e.g. "xxxx"), clear it — treat as unconfigured
          if (/^x+$/i.test(value.trim())) {
            if (key in updatedHeaders) {
              delete updatedHeaders[key];
              needsUpdate = true;
            }
            if (key in updatedDimensionValues) {
              delete updatedDimensionValues[key];
              dimensionNeedsUpdate = true;
            }
          } else {
            // Append " Kerb" to the value for the header
            const expectedHeader = value.toLowerCase().includes("kerb")
              ? value
              : `${value} Kerb`;
            if (updatedHeaders[key] !== expectedHeader) {
              updatedHeaders[key] = expectedHeader;
              needsUpdate = true;
            }
          }
        } else if (
          simpleValueColumns.includes(key) ||
          dbBaseAndBinderColumns.includes(key) ||
          sandLayingCourseColumns.includes(key)
        ) {
          // Handle simple value columns, DB base/binder columns, and sand laying course - the value is the label itself
          if (updatedHeaders[key] !== value) {
            updatedHeaders[key] = value;
            needsUpdate = true;
          }
        }
      }
    });

    if (needsUpdate) {
      setColumnHeaders(updatedHeaders);
    }
    if (dimensionNeedsUpdate) {
      setDimensionValues(updatedDimensionValues);
    }
  }, [dimensionValues, isInitialLoadComplete, columnHeaders]);

  useEffect(() => {
  }, [columnHeaders]);

  const createEmptyRow = (key?: string): PlotDrivesRow => {
    const row: any = {
      key: key || Date.now().toString(),
      plotNo: "",
      housetype: "",
    };
    for (let i = 1; i <= 77; i++) {
      row[`col${i}`] = 0;
    }
    return row;
  };

  const handleInputChange = (
    key: string,
    field: keyof PlotDrivesRow,
    value: string | number
  ) => {
    setData((prevData) => {
      const currentRow = prevData.find((row) => row.key === key);
      const oldValue = currentRow ? currentRow[field] : undefined;

      // Track the field change
      // trackFieldChange(field as string, oldValue, value, key);

      const newData = prevData.map((row) => {
        if (row.key === key) {
          return { ...row, [field]: value };
        }
        return row;
      });

      // Update tender context
      updateField("groundworks", {
        ...tender.groundworks,
        plotDrivesTable: newData,
      });
      return newData;
    });
  };

  const addRow = () => {
    const newRow = createEmptyRow();
    // trackRowAdded(newRow.key);
    const newData = [...data, newRow];
    setData(newData);
    updateField("groundworks", {
      ...tender.groundworks,
      plotDrivesTable: newData,
    });
  };

  const deleteRow = (key: string) => {
    setKeyToDelete(key);
    setKeysToDelete([]);
    setDeleteModalVisible(true);
  };

  const deleteSelectedRows = () => {
    setKeysToDelete(selectedPlotDriveKeys as string[]);
    setKeyToDelete(null);
    setDeleteModalVisible(true);
  };

  const confirmDelete = () => {
    const keysBeingDeleted = keysToDelete.length > 0 ? keysToDelete : keyToDelete ? [keyToDelete] : [];
    if (keysBeingDeleted.length > 0) {
      // trackRowDeleted(keyToDelete);
      const keySet = new Set(keysBeingDeleted);
      setData((prevData) => {
        const newData = prevData.filter((row) => !keySet.has(row.key));
        // Update tender context with new data
        // Deleting the last remaining row also clears the ad-hoc extra items
        // grafted onto Plot Drives Bill Summary — see CutFills.tsx's identical
        // fix for the reasoning.
        updateField("groundworks", {
          ...tender.groundworks,
          plotDrivesTable: newData,
          ...(newData.length === 0 && { plotDrivesExtraItems: {} }),
        });
        return newData;
      });
      setSelectedPlotDriveKeys([]);
    }
    setDeleteModalVisible(false);
    setKeyToDelete(null);
    setKeysToDelete([]);
  };

  const cancelDelete = () => {
    setDeleteModalVisible(false);
    setKeyToDelete(null);
    setKeysToDelete([]);
  };

  const handleSave = () => {
    saveTender();
    // Note: Don't reset changes here - changes persist until "Send for Review"
  };

  const handleSaveAndContinue = async () => {
    try {
      message.loading({ content: "Saving tender...", key: "saveTender", duration: 0 });

      // Update tender state with hook values
      updateField("groundworks", {
        ...tender.groundworks,
        adjustments: {
          preliminaries: groundworksTotals.preliminariesAmount,
          ohp: groundworksTotals.ohpAmount,
          discount: groundworksTotals.discountAmount,
          includePreliminaries: groundworksTotals.preliminariesPercent > 0,
          includeOhp: groundworksTotals.ohpPercent > 0,
          includeDiscount: groundworksTotals.discountPercent > 0,
        },
        totals: {
          ...tender.groundworks?.totals,
          grandTotal: groundworksTotals.grandTotal,
          totalWithAdjustments: groundworksTotals.tenderTotal,
          tenderTotal: groundworksTotals.tenderTotal,
        },
      });

      updateField("globalSettings", {
        ...tender.globalSettings,
        groundworks: {
          overheadAndProfit: groundworksTotals.ohpPercent,
          preliminaries: groundworksTotals.preliminariesPercent,
          discount: groundworksTotals.discountPercent,
        },
      });

      updateField("status", "In-progress");
      updateField("lastModified", new Date());

      await saveTender();
      await new Promise((resolve) => setTimeout(resolve, 200));

      await forceSaveBothBoqEndpoints(tender, "Save and continue from Plot Drives", {
        grandTotal: groundworksTotals.grandTotal,
        grandTotalWithAdjustments: groundworksTotals.grandTotalWithAdjustments,
        preliminariesAmount: groundworksTotals.preliminariesAmount,
        ohpAmount: groundworksTotals.ohpAmount,
        discountAmount: groundworksTotals.discountAmount,
        tenderTotal: groundworksTotals.tenderTotal,
        preliminariesPercent: groundworksTotals.preliminariesPercent,
        ohpPercent: groundworksTotals.ohpPercent,
        discountPercent: groundworksTotals.discountPercent,
      }, user?.name, itemLibraryData);

      message.destroy("saveTender");
      setTimeout(() => navigate("/groundworks/plot-service"), 500);
    } catch (error: any) {
      console.error("❌ Error submitting tender:", error);
      message.error({
        content: `Failed to submit tender: ${
          error.response?.data?.message || error.message || "Unknown error"
        }`,
        key: "saveTender",
        duration: 5,
      });
    }
  };

  const handleSaveAndExit = async () => {
    try {
      message.loading({ content: "Saving tender...", key: "saveTender", duration: 0 });

      // Update tender state with hook values
      updateField("groundworks", {
        ...tender.groundworks,
        adjustments: {
          preliminaries: groundworksTotals.preliminariesAmount,
          ohp: groundworksTotals.ohpAmount,
          discount: groundworksTotals.discountAmount,
          includePreliminaries: groundworksTotals.preliminariesPercent > 0,
          includeOhp: groundworksTotals.ohpPercent > 0,
          includeDiscount: groundworksTotals.discountPercent > 0,
        },
        totals: {
          ...tender.groundworks?.totals,
          grandTotal: groundworksTotals.grandTotal,
          totalWithAdjustments: groundworksTotals.tenderTotal,
          tenderTotal: groundworksTotals.tenderTotal,
        },
      });

      updateField("globalSettings", {
        ...tender.globalSettings,
        groundworks: {
          overheadAndProfit: groundworksTotals.ohpPercent,
          preliminaries: groundworksTotals.preliminariesPercent,
          discount: groundworksTotals.discountPercent,
        },
      });

      updateField("status", "In-progress");
      updateField("lastModified", new Date());

      await saveTender();
      await new Promise((resolve) => setTimeout(resolve, 200));

      await forceSaveBothBoqEndpoints(tender, "Save and exit from Plot Drives", {
        grandTotal: groundworksTotals.grandTotal,
        grandTotalWithAdjustments: groundworksTotals.grandTotalWithAdjustments,
        preliminariesAmount: groundworksTotals.preliminariesAmount,
        ohpAmount: groundworksTotals.ohpAmount,
        discountAmount: groundworksTotals.discountAmount,
        tenderTotal: groundworksTotals.tenderTotal,
        preliminariesPercent: groundworksTotals.preliminariesPercent,
        ohpPercent: groundworksTotals.ohpPercent,
        discountPercent: groundworksTotals.discountPercent,
      }, user?.name, itemLibraryData);

      message.destroy("saveTender");
      setTimeout(() => navigate("/"), 500);
    } catch (error: any) {
      console.error("❌ Error submitting tender:", error);
      message.error({
        content: `Failed to submit tender: ${
          error.response?.data?.message || error.message || "Unknown error"
        }`,
        key: "saveTender",
        duration: 5,
      });
    }
  };

  const calculateTotals = () => {
    const totals: any = {};
    for (let i = 1; i <= 77; i++) {
      totals[`col${i}`] = 0;
    }

    data.forEach((row) => {
      for (let i = 1; i <= 77; i++) {
        totals[`col${i}`] +=
          Math.round((Number(row[`col${i}`]) || 0) * 1000) / 1000;
      }
    });

    return totals;
  };

  // Helper function to get column title - uses dynamic header if available, otherwise falls back to default
  const getColumnTitle = React.useCallback(
    (colKey: string, defaultTitle: string): string => {
      // Kerb columns - use stored header or default as-is.
      if (["col43", "col44", "col45"].includes(colKey)) {
        return columnHeaders[colKey] || defaultTitle;
      }

      // When a stored column header exists (set from an Excel import or configure
      // modal apply), return it exactly as stored so the table shows the same text
      // as the imported sheet.
      if (columnHeaders[colKey]) {
        return columnHeaders[colKey];
      }

      // Fallback: build the header by substituting the dimension value into the
      // default title pattern (e.g. "xxxmm Sub base" → "150mm Sub base").
      // Apply normaliseDecimalMmInLabel only here so that a decimal dimension
      // (e.g. "0.150" entered in metres) is displayed as "150mm" rather than
      // the raw substituted "0.150mm".
      if (dimensionValues[colKey] && dimensionValues[colKey].trim() !== "") {
        if (dimensionValues[colKey].startsWith("ALP")) {
          return defaultTitle;
        }

        let title = defaultTitle;
        title = title.replace(/\b(x{2,}(?:mm)?)\b/gi, (match) => {
          let value = dimensionValues[colKey];
          if (
            match.toLowerCase().endsWith("mm") &&
            !value.toLowerCase().endsWith("mm")
          ) {
            value = value + "mm";
          }
          return value;
        });
        return normaliseDecimalMmInLabel(title);
      }

      return defaultTitle;
    },
    [dimensionValues, columnHeaders]
  );

  // Helper function to get column unit based on column key
  const getColumnUnit = (colKey: string): string => {
    // Linear-metre columns: edgings, kerbs, key kerb, remove timber edgings
    const mColumns = ["col39", "col40", "col41", "col42", "col43", "col44", "col45", "col46", "col48"];
    if (mColumns.includes(colKey)) return "m";
    // Count columns are "Nr"
    const nrColumns = ["col47", "col74", "col75", "col76", "col77"]; // Recessed C&F, Adjust Ironwork, Drive Demarcation
    return nrColumns.includes(colKey) ? "Nr" : "m²";
  };

  // Helper function to create column title with unit display
  const createColumnTitleWithUnit = React.useCallback(
    (colKey: string, defaultTitle: string) => {
      const title = getColumnTitle(colKey, defaultTitle);
      const unit = getColumnUnit(colKey);

      return (
        <div className="text-center">
          <Tooltip title={title}>
            <div className="gw-header-title">{title}</div>
          </Tooltip>
          <div className="gw-header-unit text-xs text-gray-500">{unit}</div>
        </div>
      );
    },
    [getColumnTitle]
  );

  const baseColumns = React.useMemo(
    () => [
      {
        title: "Plot No",
        dataIndex: "plotNo",
        key: "plotNo",
        width: 110,
        fixed: "left" as const,
        render: (text: string, record: PlotDrivesRow) => (
          <BufferedTextInput
            value={text}
            onCommit={(newValue) => {
              if (isDuplicatePlotNo(dataRef.current, record.key, newValue)) {
                message.error(`Plot No "${newValue}" is already in use.`);
                return;
              }
              handleInputChange(record.key, "plotNo", newValue);
            }}
            placeholder="Plot No"
          />
        ),
      },
      {
        title: "Housetype",
        dataIndex: "housetype",
        key: "housetype",
        width: 110,
        fixed: "left" as const,
        render: (text: string, record: PlotDrivesRow) => (
          <Input
            value={text}
            onChange={(e) =>
              handleInputChange(record.key, "housetype", e.target.value)
            }
            placeholder="Housetype"
          />
        ),
      },
    ],
    []
  );

  // Force table re-render when headers change or tab/stage changes
  const [tableRenderKey, setTableRenderKey] = useState(0);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState<string | null>(null);
  const [keysToDelete, setKeysToDelete] = useState<string[]>([]);
  const [selectedPlotDriveKeys, setSelectedPlotDriveKeys] = useState<React.Key[]>([]);
  const [configModalVisible, setConfigModalVisible] = useState(false);

  // Ref to track latest tender.groundworks to avoid stale closure issues in save effects
  const groundworksRef = useRef(tender?.groundworks);
  useEffect(() => {
    groundworksRef.current = tender?.groundworks;
  }, [tender?.groundworks]);

  // baseColumns (above) is memoized with an empty dependency array, so a Plot
  // No duplicate check inside its render closure must read rows via a ref,
  // not the `data` state directly (which the closure would otherwise capture
  // stale from the very first render).
  const dataRef = useRef(data);
  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  // Track previous configModalVisible state to detect when modal opens
  const prevConfigModalVisibleRef = useRef(configModalVisible);

  // Reset temp values when modal opens (configModalVisible goes from false to true)
  useEffect(() => {
    if (configModalVisible && !prevConfigModalVisibleRef.current) {
      // Modal just opened, reset temp values to current values
      setTempDimensionValues({ ...dimensionValues });
      setTempColumnHeaders({ ...columnHeaders });
    }
    prevConfigModalVisibleRef.current = configModalVisible;
  }, [configModalVisible, dimensionValues, columnHeaders]);

  const tableKey = React.useMemo(() => {
    const headerKey =
      Object.keys(columnHeaders).length > 0
        ? JSON.stringify(columnHeaders)
        : "default";
    return `${headerKey}-${activeTab}-${driveStage}-${tableRenderKey}`;
  }, [columnHeaders, activeTab, driveStage, tableRenderKey]);

  // Trigger re-render after tab change to recalculate offsetHeader
  useEffect(() => {
    // Small delay to allow dimension card to render and get correct height
    const timer = setTimeout(() => {
      setTableRenderKey((prev) => prev + 1);
    }, 50);
    return () => clearTimeout(timer);
  }, [activeTab, driveStage]);

  // Mapping of columns to drive types
  const columnToDriveTypeMap: Record<string, string[]> = {
    privateMacadamBinder: ["col3", "col4", "col5"], // xxxmm Sub base, xxmm DB Base, xxmm DB Binder
    privateMacadamSurface: ["col50", "col51", "col52"], // xxmm DB Surface, xxmm HRA Surface, xxmm SMA Surface
    privateBlockBinder: ["col9", "col10", "col11"], // xxxmm thick MOT Type 1, xxmm DB Base, xxmm DB Binder
    privateBlockSurface: ["col55", "col56", "col57"], // sand Laying Course, 60mm/80mm Concrete paviors
    privatePermeableBinder: ["col16", "col17", "col18"], // xxmm DB binder Running course, xxmm 10-63mm aggregate, xxmm 5-20mm aggregate
    privatePermeableSurface: ["col59", "col60", "col61"], // xxmm thick 2-6mm laying Aggregate, 60mm/80mm Concrete paviors
    sharedMacadamBinder: ["col22", "col23", "col24"], // xxxmm Sub base, xxmm DB Base, xxmm DB Binder
    sharedMacadamSurface: ["col63", "col64", "col65"], // xxmm DB Surface, xxmm HRA Surface, xxmm SMA Surface
    sharedBlockBinder: ["col28", "col29", "col30"], // xxxmm Sub base, xxmm DB Base, xxmm DB Binder
    sharedBlockSurface: ["col67", "col68", "col69"], // sand Laying Course, 60mm/80mm Concrete paviors
    sharedPermeableBinder: [
      "col35",
      "col36",
      "col37",
    ], // xxmm DB binder Running course, xxmm aggregates
    kerbsAndEdgingsBinder: ["col43", "col44", "col45"], // xxxx Kerb (dynamic)
    sharedPermeableSurface: ["col71", "col72", "col73"], // xxmm thick 2-6mm laying Aggregate, 60mm/80mm Concrete paviors
  };

  // Extract columns that contain dimension placeholders (xxmm, xxxmm, etc.) or special dropdown columns
  const dimensionFields = React.useMemo(() => {
    const fieldsWithDimensions: Array<{
      key: string;
      title: string;
      driveTypes: string[];
    }> = [];

    // Special columns that need dropdowns but don't have dimension patterns in their title
    const specialDropdownColumns: string[] = [
      "col56", "col57", // Private Block Paved Surface: 60mm/80mm Concrete paviors
      "col60", "col61", // Private Permeable Paving Surface: 60mm/80mm Concrete paviors
      "col68", "col69", // Shared Block Paved Surface: 60mm/80mm Concrete paviors
      "col72", "col73", // Shared Permeable Paving Surface: 60mm/80mm Concrete paviors
    ];

    // Kerb columns that should appear in configure modal with text input
    const kerbColumns: string[] = ["col43", "col44", "col45"];

    // Columns that should NOT be dynamic/configurable (static fields)
    const staticColumns: string[] = []; // All columns with xxmm pattern are now dynamic

    // Get all columns from excelColumns that have dimension patterns in their DEFAULT title
    excelColumns.forEach((col) => {
      // Skip static columns - they should not be configurable
      if (staticColumns.includes(col.key)) {
        return;
      }

      // Always check against the DEFAULT title to detect dimension patterns
      const defaultTitle = col.title;
      // Check if DEFAULT header contains dimension patterns like xxmm, xxxmm, xxxx
      // OR if it's a special dropdown column OR if it's a kerb column
      if (
        /\b(x{2,}(?:mm)?)\b/gi.test(String(defaultTitle)) ||
        specialDropdownColumns.includes(col.key) ||
        kerbColumns.includes(col.key)
      ) {
        // Find which drive types this column belongs to
        const driveTypes: string[] = [];
        Object.entries(columnToDriveTypeMap).forEach(([driveType, columns]) => {
          if (columns.includes(col.key)) {
            driveTypes.push(driveType);
          }
        });

        // For kerb columns: show configured header or blank (not the default "xxxx Kerb")
        // For other columns: always use the default title with dimension placeholders (e.g., "xxmm Sub base")
        const displayTitle = kerbColumns.includes(col.key)
          ? (columnHeaders[col.key] ?? "")
          : defaultTitle;

        fieldsWithDimensions.push({
          key: col.key,
          title: String(displayTitle),
          driveTypes,
        });
      }
    });

    return fieldsWithDimensions;
  }, [columnHeaders]);

  // Function to create dimension fields card for specific drive type
  const getDimensionFieldsCard = (driveType: string) => {
    const filteredFields = dimensionFields.filter((field) =>
      field.driveTypes.includes(driveType)
    );

    if (filteredFields.length === 0) return null;

    // Helper to handle sub base input change (updates temporary state only)
    const handleSubBaseChange = (fieldKey: string, value: string) => {
      if (!value || value === "0") {
        setTempDimensionValues((prev) => {
          const newValues = { ...prev };
          delete newValues[fieldKey];
          return newValues;
        });
        setTempColumnHeaders((prev) => {
          const newHeaders = { ...prev };
          delete newHeaders[fieldKey];
          return newHeaders;
        });
      } else {
        const metresValue = parseFloat(value);
        // Input is in metres (0.xxx); convert to whole mm for storage and header
        const mmValue = metresValue < 1
          ? String(Math.round(metresValue * 1000))
          : String(Math.round(metresValue));
        setTempDimensionValues((prev) => ({ ...prev, [fieldKey]: mmValue }));
        setTempColumnHeaders((prev) => ({
          ...prev,
          [fieldKey]: `${mmValue}mm thick MOT Type 1`,
        }));
      }
    };

    // Helper to handle Additional/Capping input change (updates temporary state only)
    const handleCappingChange = (fieldKey: string, value: string) => {
      if (!value) {
        setTempDimensionValues((prev) => {
          const newValues = { ...prev };
          delete newValues[fieldKey];
          return newValues;
        });
        setTempColumnHeaders((prev) => {
          const newHeaders = { ...prev };
          delete newHeaders[fieldKey];
          return newHeaders;
        });
      } else {
        setTempDimensionValues((prev) => ({ ...prev, [fieldKey]: value }));
        const formattedValue = value.includes("mm") ? value : `${value}mm`;
        setTempColumnHeaders((prev) => ({
          ...prev,
          [fieldKey]: `${formattedValue} Additional / Capping`,
        }));
      }
    };

    // Helper to handle kerb header change (updates temporary state only)
    const handleKerbHeaderChange = (fieldKey: string, value: string) => {
      if (!value) {
        setTempDimensionValues((prev) => {
          const newValues = { ...prev };
          delete newValues[fieldKey];
          return newValues;
        });
        setTempColumnHeaders((prev) => {
          const newHeaders = { ...prev };
          delete newHeaders[fieldKey];
          return newHeaders;
        });
      } else {
        setTempDimensionValues((prev) => ({ ...prev, [fieldKey]: value }));
        // Append " Kerb" to the value for the header
        setTempColumnHeaders((prev) => ({
          ...prev,
          [fieldKey]: `${value} Kerb`,
        }));
      }
    };

    // Helper to handle DB binder Running course select change (updates temporary state only)
    const handleBinderRunningCourseChange = (
      fieldKey: string,
      value: string
    ) => {
      if (!value) {
        setTempDimensionValues((prev) => {
          const newValues = { ...prev };
          delete newValues[fieldKey];
          return newValues;
        });
        setTempColumnHeaders((prev) => {
          const newHeaders = { ...prev };
          delete newHeaders[fieldKey];
          return newHeaders;
        });
      } else {
        setTempDimensionValues((prev) => ({ ...prev, [fieldKey]: value }));
        setTempColumnHeaders((prev) => ({
          ...prev,
          [fieldKey]: value,
        }));
      }
    };

    // DB binder Running course dropdown options
    const binderRunningCourseOptions = [
      "50mm thick AC 20mm dense bin",
      "60mm thick AC 20mm dense bin",
      "75mm thick AC 20mm dense bin",
      "80mm thick AC 20mm dense bin",
      "100mm thick AC 20mm dense bin",
    ];

    // Helper to handle aggregate sub base numeric input change (updates temporary state only)
    // Value received is in metres (e.g. 0.200); stored and formatted in mm (e.g. "200")
    const handleAggregateSubBaseChange = (fieldKey: string, value: string) => {
      if (!value || value === "0") {
        setTempDimensionValues((prev) => {
          const newValues = { ...prev };
          delete newValues[fieldKey];
          return newValues;
        });
        setTempColumnHeaders((prev) => {
          const newHeaders = { ...prev };
          delete newHeaders[fieldKey];
          return newHeaders;
        });
      } else {
        const metresValue = parseFloat(value);
        const mmValue = metresValue < 1
          ? String(Math.round(metresValue * 1000))
          : String(Math.round(metresValue));
        setTempDimensionValues((prev) => ({ ...prev, [fieldKey]: mmValue }));
        setTempColumnHeaders((prev) => ({
          ...prev,
          [fieldKey]: `${mmValue}mm thick 63-10mm Sub Base - Varied Depths`,
        }));
      }
    };

    // Helper to handle 5-20mm aggregate sub base numeric input change (updates temporary state only)
    // Value received is in metres (e.g. 0.200); stored and formatted in mm (e.g. "200")
    const handleAggregate520SubBaseChange = (fieldKey: string, value: string) => {
      if (!value || value === "0") {
        setTempDimensionValues((prev) => {
          const newValues = { ...prev };
          delete newValues[fieldKey];
          return newValues;
        });
        setTempColumnHeaders((prev) => {
          const newHeaders = { ...prev };
          delete newHeaders[fieldKey];
          return newHeaders;
        });
      } else {
        const metresValue = parseFloat(value);
        const mmValue = metresValue < 1
          ? String(Math.round(metresValue * 1000))
          : String(Math.round(metresValue));
        setTempDimensionValues((prev) => ({ ...prev, [fieldKey]: mmValue }));
        setTempColumnHeaders((prev) => ({
          ...prev,
          [fieldKey]: `${mmValue}mm thick 5-20mm clean aggregate sub base`,
        }));
      }
    };

    // Helper to handle 2-6mm laying Aggregate numeric input change (updates temporary state only)
    // Value received is in metres (e.g. 0.666); stored and formatted in mm (e.g. "666")
    const handleLayingAggregateChange = (fieldKey: string, value: string) => {
      if (!value || value === "0") {
        setTempDimensionValues((prev) => {
          const newValues = { ...prev };
          delete newValues[fieldKey];
          return newValues;
        });
        setTempColumnHeaders((prev) => {
          const newHeaders = { ...prev };
          delete newHeaders[fieldKey];
          return newHeaders;
        });
      } else {
        const metresValue = parseFloat(value);
        // Input is in metres (0.xxx); convert to whole mm for storage and header
        const mmValue = metresValue < 1
          ? String(Math.round(metresValue * 1000))
          : String(Math.round(metresValue));
        setTempDimensionValues((prev) => ({ ...prev, [fieldKey]: mmValue }));
        setTempColumnHeaders((prev) => ({
          ...prev,
          [fieldKey]: `${mmValue}mm thick 2-6mm laying Aggregate`,
        }));
      }
    };

    // Helper to handle select change (updates temporary state only)
    const handleSelectChange = (fieldKey: string, value: string) => {
      if (!value) {
        setTempDimensionValues((prev) => {
          const newValues = { ...prev };
          delete newValues[fieldKey];
          return newValues;
        });
        setTempColumnHeaders((prev) => {
          const newHeaders = { ...prev };
          delete newHeaders[fieldKey];
          return newHeaders;
        });
      } else {
        setTempDimensionValues((prev) => ({ ...prev, [fieldKey]: value }));
        setTempColumnHeaders((prev) => ({ ...prev, [fieldKey]: value }));
      }
    };

    // Helper to handle surface course select change (stores evaluate code in dimensionValues, label in columnHeaders)
    const handleSurfaceCourseChange = (fieldKey: string, value: string) => {
      if (!value) {
        setTempDimensionValues((prev) => {
          const newValues = { ...prev };
          delete newValues[fieldKey];
          return newValues;
        });
        setTempColumnHeaders((prev) => {
          const newHeaders = { ...prev };
          delete newHeaders[fieldKey];
          return newHeaders;
        });
      } else {
        // Store evaluate code in dimensionValues
        setTempDimensionValues((prev) => ({ ...prev, [fieldKey]: value }));
        // Store label in columnHeaders
        const selectedCode = value as EvaluateCode;
        const label = EVALUATE_CODES[selectedCode]?.label || value;
        setTempColumnHeaders((prev) => ({ ...prev, [fieldKey]: label }));
      }
    };

    // Handler for DB/HRA/SMA surface variants. The select binds to the variant
    // label (unique per option). On change we store the underlying EvaluateCode
    // in dimensionValues (so downstream item-library resolution is unchanged)
    // and the variant label in columnHeaders (so the chosen variant persists).
    const handleSurfaceVariantChange = (
      fieldKey: string,
      label: string,
      variants: { code: EvaluateCode; label: string }[],
    ) => {
      if (!label) {
        setTempDimensionValues((prev) => {
          const newValues = { ...prev };
          delete newValues[fieldKey];
          return newValues;
        });
        setTempColumnHeaders((prev) => {
          const newHeaders = { ...prev };
          delete newHeaders[fieldKey];
          return newHeaders;
        });
        return;
      }
      const variant = variants.find((v) => v.label === label);
      if (!variant) return;
      setTempDimensionValues((prev) => ({ ...prev, [fieldKey]: variant.code }));
      setTempColumnHeaders((prev) => ({ ...prev, [fieldKey]: variant.label }));
    };

    // DB Base options
    const dbBaseOptions = Object.values(EVALUATE_CODES).filter((layer) =>
      ["ALP041", "ALP042"].includes(layer.code)
    );

    // DB Binder options
    const dbBinderOptions = Object.values(EVALUATE_CODES).filter((layer) =>
      ["ALP043", "ALP044", "ALP045", "ALP046", "ALP047"].includes(layer.code)
    );

    // DB Surface variants — separate dropdown options per thickness/aggregate combo
    // that all resolve to the same underlying EvaluateCode.
    const dbSurfaceVariants: { code: EvaluateCode; label: string }[] = [
      { code: "ALP048", label: "20mm thick AC 6mm dense surf" },
      { code: "ALP049", label: "25mm thick AC 6mm dense surf" },
      { code: "ALP049", label: "25mm thick AC 10mm dense surf" },
      { code: "ALP051", label: "30mm thick AC 6mm dense surf" },
      { code: "ALP051", label: "30mm thick AC 10mm dense surf" },
    ];

    // HRA Surface variants — all four map to ALP053.
    const hraSurfaceVariants: { code: EvaluateCode; label: string }[] = [
      { code: "ALP053", label: "25mm thick HRA 6mm" },
      { code: "ALP053", label: "25mm thick HRA 10mm" },
      { code: "ALP053", label: "30mm thick HRA 6mm" },
      { code: "ALP053", label: "30mm thick HRA 10mm" },
    ];

    // SMA Surface variants — all four map to ALP052.
    const smaSurfaceVariants: { code: EvaluateCode; label: string }[] = [
      { code: "ALP052", label: "25mm thick SMA 6mm" },
      { code: "ALP052", label: "25mm thick SMA 10mm" },
      { code: "ALP052", label: "30mm thick SMA 6mm" },
      { code: "ALP052", label: "30mm thick SMA 10mm" },
    ];

    // Surface Course options (legacy fallback)
    const surfaceCourseOptions = Object.values(EVALUATE_CODES).filter((layer) =>
      ["ALP048", "ALP049", "ALP051", "ALP052"].includes(layer.code)
    );

    // 60mm Block Paviors options
    const block60mmPaviorsOptions = Object.values(EVALUATE_CODES).filter(
      (layer) =>
        ["GWALPD002", "GWALPD010", "GWALPD006"].includes(layer.code)
    );

    // 80mm Block Paviors options
    const block80mmPaviorsOptions = Object.values(EVALUATE_CODES).filter(
      (layer) =>
        ["GWALPD001", "GWALPD009", "GWALPD005"].includes(layer.code)
    );

    // Sand Laying Course options
    const sandLayingCourseOptions = [
      "30mm Sand Laying Course",
      "50mm Sand Laying Course",
    ];

    // 60mm Permeable Paviors options
    const permeable60mmPaviorsOptions = Object.values(EVALUATE_CODES).filter(
      (layer) =>
        ["GWALPD004", "GWALPD008"].includes(layer.code)
    );

    // 80mm Permeable Paviors options
    const permeable80mmPaviorsOptions = Object.values(EVALUATE_CODES).filter(
      (layer) =>
        ["GWALPD003", "GWALPD007"].includes(layer.code)
    );

    // Determine field type for rendering
    const getFieldType = (
      key: string
    ):
      | "subbase"
      | "capping"
      | "dbbase"
      | "dbbinder"
      | "binderRunningCourse"
      | "aggregateSubBase"
      | "aggregate520SubBase"
      | "layingAggregate"
      | "dbSurface"
      | "hraSurface"
      | "smaSurface"
      | "sandLayingCourse"
      | "60mmBlockPaviors"
      | "80mmBlockPaviors"
      | "60mmPermeablePaviors"
      | "80mmPermeablePaviors"
      | "surface"
      | "kerb" => {
      if (["col3", "col9", "col22", "col28"].includes(key)) return "subbase";
      if (["col16", "col35"].includes(key)) return "binderRunningCourse";
      if (["col17", "col36"].includes(key)) return "aggregateSubBase";
      if (["col59", "col71"].includes(key)) return "layingAggregate"; // xxmm 2-6mm Private/Shared Permeable
      if (["col4", "col10", "col23", "col29"].includes(key)) return "dbbase";
      if (["col18", "col37"].includes(key)) return "aggregate520SubBase";
      if (
        [
          "col5",
          "col11",
          "col24",
          "col30",
        ].includes(key)
      )
        return "dbbinder";
      if (["col50", "col63"].includes(key)) return "dbSurface";
      if (["col51", "col64"].includes(key)) return "hraSurface";
      if (["col52", "col65"].includes(key)) return "smaSurface";
      if (["col55", "col67"].includes(key)) return "sandLayingCourse";
      if (["col56", "col68"].includes(key)) return "60mmBlockPaviors";
      if (["col57", "col69"].includes(key)) return "80mmBlockPaviors";
      if (["col43", "col44", "col45"].includes(key)) return "kerb";
      if (["col60", "col72"].includes(key)) return "60mmPermeablePaviors";
      if (["col61", "col73"].includes(key)) return "80mmPermeablePaviors";
      return "surface";
    };

    return (
      <div className="grid grid-cols-2 gap-3">
        {filteredFields.map((field) => {
          const fieldType = getFieldType(field.key);
          return (
            <div key={field.key}>
              <Text className="text-xs mb-1 block">{field.title}</Text>
              {fieldType === "subbase" ? (
                <NumberInput
                  value={(() => {
                    const raw = parseFloat(tempDimensionValues[field.key]) || 0;
                    // Stored as mm (e.g. 150); display in metres (e.g. 0.150)
                    return raw >= 1 ? raw / 1000 : raw;
                  })()}
                  onChange={(value) =>
                    handleSubBaseChange(field.key, value?.toString() || "")
                  }
                  positiveOnly
                  decimalPlaces={3}
                  zeroIntegerOnly
                />
              ) : fieldType === "capping" ? (
                <NumberInput
                  value={
                    tempDimensionValues[field.key]
                      ? parseFloat(tempDimensionValues[field.key])
                      : 0
                  }
                  onChange={(value) =>
                    handleCappingChange(field.key, value?.toString() || "")
                  }
                  positiveOnly
                  decimalPlaces={3}
                  zeroIntegerOnly
                />
              ) : fieldType === "binderRunningCourse" ? (
                <select
                  value={tempDimensionValues[field.key] || ""}
                  onChange={(e) =>
                    handleBinderRunningCourseChange(field.key, e.target.value)
                  }
                  style={selectStyles}
                >
                  <option value="">Select</option>
                  {binderRunningCourseOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              ) : fieldType === "aggregateSubBase" ? (
                <NumberInput
                  value={(() => {
                    const raw = parseFloat(tempDimensionValues[field.key]) || 0;
                    return raw >= 1 ? raw / 1000 : raw;
                  })()}
                  onChange={(value) =>
                    handleAggregateSubBaseChange(
                      field.key,
                      value?.toString() || ""
                    )
                  }
                  positiveOnly
                  decimalPlaces={3}
                  zeroIntegerOnly
                />
              ) : fieldType === "aggregate520SubBase" ? (
                <NumberInput
                  value={(() => {
                    const raw = parseFloat(tempDimensionValues[field.key]) || 0;
                    return raw >= 1 ? raw / 1000 : raw;
                  })()}
                  onChange={(value) =>
                    handleAggregate520SubBaseChange(
                      field.key,
                      value?.toString() || ""
                    )
                  }
                  positiveOnly
                  decimalPlaces={3}
                  zeroIntegerOnly
                />
              ) : fieldType === "layingAggregate" ? (
                <NumberInput
                  value={(() => {
                    const raw = parseFloat(tempDimensionValues[field.key]) || 0;
                    // Stored as mm (e.g. 666); display in metres (e.g. 0.666)
                    return raw >= 1 ? raw / 1000 : raw;
                  })()}
                  onChange={(value) =>
                    handleLayingAggregateChange(
                      field.key,
                      value?.toString() || ""
                    )
                  }
                  positiveOnly
                  decimalPlaces={3}
                  zeroIntegerOnly
                />
              ) : fieldType === "kerb" ? (
                <TextInput
                  value={/^x+$/i.test((tempDimensionValues[field.key] ?? "").trim()) ? "" : (tempDimensionValues[field.key] || "")}
                  onChange={(value) =>
                    handleKerbHeaderChange(field.key, value)
                  }
                  placeholder="Enter value"
                  maxLength={100}
                />
              ) : fieldType === "dbSurface" ? (
                <select
                  value={tempColumnHeaders[field.key] || ""}
                  onChange={(e) =>
                    handleSurfaceVariantChange(field.key, e.target.value, dbSurfaceVariants)
                  }
                  style={selectStyles}
                >
                  <option value="">Select</option>
                  {dbSurfaceVariants.map((v) => (
                    <option key={v.label} value={v.label}>
                      {v.label}
                    </option>
                  ))}
                </select>
              ) : fieldType === "hraSurface" ? (
                <select
                  value={tempColumnHeaders[field.key] || ""}
                  onChange={(e) =>
                    handleSurfaceVariantChange(field.key, e.target.value, hraSurfaceVariants)
                  }
                  style={selectStyles}
                >
                  <option value="">Select</option>
                  {hraSurfaceVariants.map((v) => (
                    <option key={v.label} value={v.label}>
                      {v.label}
                    </option>
                  ))}
                </select>
              ) : fieldType === "smaSurface" ? (
                <select
                  value={tempColumnHeaders[field.key] || ""}
                  onChange={(e) =>
                    handleSurfaceVariantChange(field.key, e.target.value, smaSurfaceVariants)
                  }
                  style={selectStyles}
                >
                  <option value="">Select</option>
                  {smaSurfaceVariants.map((v) => (
                    <option key={v.label} value={v.label}>
                      {v.label}
                    </option>
                  ))}
                </select>
              ) : fieldType === "sandLayingCourse" ? (
                <select
                  value={tempDimensionValues[field.key] || ""}
                  onChange={(e) =>
                    handleSelectChange(field.key, e.target.value)
                  }
                  style={selectStyles}
                >
                  <option value="">Select</option>
                  {sandLayingCourseOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              ) : fieldType === "60mmBlockPaviors" ? (
                <select
                  value={tempDimensionValues[field.key] || ""}
                  onChange={(e) =>
                    handleSurfaceCourseChange(field.key, e.target.value)
                  }
                  style={selectStyles}
                >
                  <option value="">Select</option>
                  {block60mmPaviorsOptions.map((layer) => (
                    <option key={layer.code} value={layer.code}>
                      {layer.label}
                    </option>
                  ))}
                </select>
              ) : fieldType === "80mmBlockPaviors" ? (
                <select
                  value={tempDimensionValues[field.key] || ""}
                  onChange={(e) =>
                    handleSurfaceCourseChange(field.key, e.target.value)
                  }
                  style={selectStyles}
                >
                  <option value="">Select</option>
                  {block80mmPaviorsOptions.map((layer) => (
                    <option key={layer.code} value={layer.code}>
                      {layer.label}
                    </option>
                  ))}
                </select>
              ) : fieldType === "60mmPermeablePaviors" ? (
                <select
                  value={tempDimensionValues[field.key] || ""}
                  onChange={(e) =>
                    handleSurfaceCourseChange(field.key, e.target.value)
                  }
                  style={selectStyles}
                >
                  <option value="">Select</option>
                  {permeable60mmPaviorsOptions.map((layer) => (
                    <option key={layer.code} value={layer.code}>
                      {layer.label}
                    </option>
                  ))}
                </select>
              ) : fieldType === "80mmPermeablePaviors" ? (
                <select
                  value={tempDimensionValues[field.key] || ""}
                  onChange={(e) =>
                    handleSurfaceCourseChange(field.key, e.target.value)
                  }
                  style={selectStyles}
                >
                  <option value="">Select</option>
                  {permeable80mmPaviorsOptions.map((layer) => (
                    <option key={layer.code} value={layer.code}>
                      {layer.label}
                    </option>
                  ))}
                </select>
              ) : (
                <select
                  value={tempDimensionValues[field.key] || ""}
                  onChange={(e) =>
                    handleSelectChange(field.key, e.target.value)
                  }
                  style={selectStyles}
                >
                  <option value="">Select</option>
                  {(fieldType === "dbbase"
                    ? dbBaseOptions
                    : fieldType === "dbbinder"
                    ? dbBinderOptions
                    : surfaceCourseOptions
                  ).map((layer) => (
                    <option key={layer.code} value={layer.label}>
                      {layer.label}
                    </option>
                  ))}
                </select>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // BINDER STAGE COLUMNS
  const macadamBinderColumns = React.useMemo(
    () => [
      ...baseColumns,
      {
        title: createColumnTitleWithUnit("col1", "Level and compact formation"),
        dataIndex: "col1",
        key: "col1",
        width: 110,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col1", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: createColumnTitleWithUnit("col2", "Geotextile Layer"),
        dataIndex: "col2",
        key: "col2",
        width: 110,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col2", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: createColumnTitleWithUnit("col3", "xxxmm thick MOT Type 1"),
        dataIndex: "col3",
        key: "col3",
        width: 110,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col3", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: createColumnTitleWithUnit("col4", "xxmm DB  Base"),
        dataIndex: "col4",
        key: "col4",
        width: 110,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col4", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: createColumnTitleWithUnit("col5", "xxmm DB Binder"),
        dataIndex: "col5",
        key: "col5",
        width: 110,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col5", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: createColumnTitleWithUnit("col6", "Sealing Grit"),
        dataIndex: "col6",
        key: "col6",
        width: 110,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col6", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: "Actions",
        key: "actions",
        width: 110,
        fixed: "right" as const,
        align: "center" as const,
        render: (_, record: PlotDrivesRow) => (
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => deleteRow(record.key)}
            size="small"
          />
        ),
      },
    ],
    [baseColumns, createColumnTitleWithUnit]
  );

  // SURFACE STAGE COLUMNS
  const macadamSurfaceColumns = React.useMemo(
    () => [
      ...baseColumns,
      {
        title: createColumnTitleWithUnit("col48", "Remove Timber edgings"),
        dataIndex: "col48",
        key: "col48",
        width: 130,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col48", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: createColumnTitleWithUnit("col49", "Sweep & Tack"),
        dataIndex: "col49",
        key: "col49",
        width: 110,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col49", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: createColumnTitleWithUnit("col50", "xxmm DB Surface course"),
        dataIndex: "col50",
        key: "col50",
        width: 130,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col50", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: createColumnTitleWithUnit("col51", "xxmm HRA Surface course"),
        dataIndex: "col51",
        key: "col51",
        width: 130,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col51", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: createColumnTitleWithUnit("col52", "xxmm SMA Surface course"),
        dataIndex: "col52",
        key: "col52",
        width: 130,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col52", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: "Actions",
        key: "actions",
        width: 110,
        fixed: "right" as const,
        align: "center" as const,
        render: (_, record: PlotDrivesRow) => (
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => deleteRow(record.key)}
            size="small"
          />
        ),
      },
    ],
    [baseColumns, createColumnTitleWithUnit]
  );

  // PRIVATE BLOCK PAVED DRIVE - BINDER STAGE COLUMNS
  const blockPavedBinderColumns = React.useMemo(
    () => [
      ...baseColumns,
      {
        title: createColumnTitleWithUnit("col7", "Level and compact formation"),
        dataIndex: "col7",
        key: "col7",
        width: 110,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col7", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: createColumnTitleWithUnit("col8", "Geotextile Layer"),
        dataIndex: "col8",
        key: "col8",
        width: 110,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col8", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: createColumnTitleWithUnit("col9", "xxxmm thick MOT Type 1"),
        dataIndex: "col9",
        key: "col9",
        width: 120,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col9", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: createColumnTitleWithUnit("col10", "xxmm DB Base"),
        dataIndex: "col10",
        key: "col10",
        width: 110,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col10", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: createColumnTitleWithUnit("col11", "xxmm DB Binder"),
        dataIndex: "col11",
        key: "col11",
        width: 110,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col11", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: createColumnTitleWithUnit("col12", "Sealing Grit"),
        dataIndex: "col12",
        key: "col12",
        width: 110,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col12", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: "Actions",
        key: "actions",
        width: 110,
        fixed: "right" as const,
        align: "center" as const,
        render: (_, record: PlotDrivesRow) => (
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => deleteRow(record.key)}
            size="small"
          />
        ),
      },
    ],
    [baseColumns, createColumnTitleWithUnit]
  );

  // PRIVATE BLOCK PAVED DRIVE - SURFACE STAGE COLUMNS
  const blockPavedSurfaceColumns = React.useMemo(
    () => [
      ...baseColumns,
      {
        title: createColumnTitleWithUnit("col53", "Sweep"),
        dataIndex: "col53",
        key: "col53",
        width: 110,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col53", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: createColumnTitleWithUnit("col54", "Puncture Macadam"),
        dataIndex: "col54",
        key: "col54",
        width: 120,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col54", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: createColumnTitleWithUnit("col55", "xxmm sand Laying Course"),
        dataIndex: "col55",
        key: "col55",
        width: 130,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col55", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: createColumnTitleWithUnit("col56", "60mm Concrete paviors"),
        dataIndex: "col56",
        key: "col56",
        width: 130,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col56", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: createColumnTitleWithUnit("col57", "80mm Concrete paviors"),
        dataIndex: "col57",
        key: "col57",
        width: 130,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col57", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: "Actions",
        key: "actions",
        width: 110,
        fixed: "right" as const,
        align: "center" as const,
        render: (_, record: PlotDrivesRow) => (
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => deleteRow(record.key)}
            size="small"
          />
        ),
      },
    ],
    [baseColumns, createColumnTitleWithUnit]
  );

  // PRIVATE PERMEABLE PAVING - BINDER STAGE COLUMNS
  const permeableBinderColumns = React.useMemo(
    () => [
      ...baseColumns,
      {
        title: createColumnTitleWithUnit("col14", "Level and compact formation"),
        dataIndex: "col14",
        key: "col14",
        width: 110,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col14", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: createColumnTitleWithUnit("col15", "Terram Layer to Bottom and Sides permeable paving"),
        dataIndex: "col15",
        key: "col15",
        width: 130,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col15", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: createColumnTitleWithUnit("col16", "xxmm DB binder Running course"),
        dataIndex: "col16",
        key: "col16",
        width: 150,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col16", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: createColumnTitleWithUnit("col17", "xxmm thick 63-10mm Sub Base - Varied Depths"),
        dataIndex: "col17",
        key: "col17",
        width: 150,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col17", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: createColumnTitleWithUnit("col18", "xxmm thick 5-20mm clean aggregate sub base"),
        dataIndex: "col18",
        key: "col18",
        width: 150,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col18", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: createColumnTitleWithUnit("col19", "Permeable Geotextile Layer"),
        dataIndex: "col19",
        key: "col19",
        width: 120,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col19", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: "Actions",
        key: "actions",
        width: 110,
        fixed: "right" as const,
        align: "center" as const,
        render: (_, record: PlotDrivesRow) => (
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => deleteRow(record.key)}
            size="small"
          />
        ),
      },
    ],
    [baseColumns, createColumnTitleWithUnit]
  );

  // PRIVATE PERMEABLE PAVING - SURFACE STAGE COLUMNS
  const permeableSurfaceColumns = React.useMemo(
    () => [
      ...baseColumns,
      {
        title: createColumnTitleWithUnit("col58", "Puncture Macadam"),
        dataIndex: "col58",
        key: "col58",
        width: 120,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col58", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: createColumnTitleWithUnit("col59", "xxmm thick 2-6mm laying Aggregate"),
        dataIndex: "col59",
        key: "col59",
        width: 160,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col59", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: createColumnTitleWithUnit("col60", "60mm Concrete paviors"),
        dataIndex: "col60",
        key: "col60",
        width: 130,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col60", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: createColumnTitleWithUnit("col61", "80mm Concrete paviors"),
        dataIndex: "col61",
        key: "col61",
        width: 130,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col61", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: "Actions",
        key: "actions",
        width: 110,
        fixed: "right" as const,
        align: "center" as const,
        render: (_, record: PlotDrivesRow) => (
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => deleteRow(record.key)}
            size="small"
          />
        ),
      },
    ],
    [baseColumns, createColumnTitleWithUnit]
  );

  // SHARED MACADAM DRIVE - BINDER STAGE COLUMNS
  const sharedMacadamBinderColumns = React.useMemo(
    () => [
      ...baseColumns,
      {
        title: createColumnTitleWithUnit("col20", "Level and compact formation"),
        dataIndex: "col20",
        key: "col20",
        width: 110,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col20", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: createColumnTitleWithUnit("col21", "Geotextile Layer"),
        dataIndex: "col21",
        key: "col21",
        width: 110,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col21", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: createColumnTitleWithUnit("col22", "xxxmm thick MOT Type 1"),
        dataIndex: "col22",
        key: "col22",
        width: 110,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col22", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: createColumnTitleWithUnit("col23", "xxmm DB  Base"),
        dataIndex: "col23",
        key: "col23",
        width: 110,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col23", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: createColumnTitleWithUnit("col24", "xxmm DB Binder"),
        dataIndex: "col24",
        key: "col24",
        width: 110,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col24", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: createColumnTitleWithUnit("col25", "Sealing Grit"),
        dataIndex: "col25",
        key: "col25",
        width: 110,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col25", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: "Actions",
        key: "actions",
        width: 110,
        fixed: "right" as const,
        align: "center" as const,
        render: (_, record: PlotDrivesRow) => (
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => deleteRow(record.key)}
            size="small"
          />
        ),
      },
    ],
    [baseColumns, createColumnTitleWithUnit]
  );

  // SHARED MACADAM DRIVE - SURFACE STAGE COLUMNS
  const sharedMacadamSurfaceColumns = React.useMemo(
    () => [
      ...baseColumns,
      {
        title: createColumnTitleWithUnit("col62", "Sweep & Tack"),
        dataIndex: "col62",
        key: "col62",
        width: 110,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col62", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: createColumnTitleWithUnit("col63", "xxmm DB Surface course"),
        dataIndex: "col63",
        key: "col63",
        width: 130,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col63", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: createColumnTitleWithUnit("col64", "xxmm HRA Surface course"),
        dataIndex: "col64",
        key: "col64",
        width: 130,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col64", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: createColumnTitleWithUnit("col65", "xxmm SMA Surface course"),
        dataIndex: "col65",
        key: "col65",
        width: 130,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col65", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: "Actions",
        key: "actions",
        width: 110,
        fixed: "right" as const,
        align: "center" as const,
        render: (_, record: PlotDrivesRow) => (
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => deleteRow(record.key)}
            size="small"
          />
        ),
      },
    ],
    [baseColumns, createColumnTitleWithUnit]
  );

  // SHARED BLOCK PAVED - BINDER STAGE COLUMNS
  const sharedBlockBinderColumns = React.useMemo(
    () => [
      ...baseColumns,
      {
        title: createColumnTitleWithUnit("col26", "Level and compact formation"),
        dataIndex: "col26",
        key: "col26",
        width: 110,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col26", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: createColumnTitleWithUnit("col27", "Geotextile Layer"),
        dataIndex: "col27",
        key: "col27",
        width: 110,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col27", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: createColumnTitleWithUnit("col28", "xxxmm thick MOT Type 1"),
        dataIndex: "col28",
        key: "col28",
        width: 110,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col28", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: createColumnTitleWithUnit("col29", "xxmm DB Base"),
        dataIndex: "col29",
        key: "col29",
        width: 110,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col29", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: createColumnTitleWithUnit("col30", "xxmm DB Binder"),
        dataIndex: "col30",
        key: "col30",
        width: 110,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col30", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: createColumnTitleWithUnit("col31", "Sealing Grit"),
        dataIndex: "col31",
        key: "col31",
        width: 110,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col31", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: "Actions",
        key: "actions",
        width: 110,
        fixed: "right" as const,
        align: "center" as const,
        render: (_, record: PlotDrivesRow) => (
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => deleteRow(record.key)}
            size="small"
          />
        ),
      },
    ],
    [baseColumns, createColumnTitleWithUnit]
  );

  // SHARED BLOCK PAVED - SURFACE STAGE COLUMNS
  const sharedBlockSurfaceColumns = React.useMemo(
    () => [
      ...baseColumns,
      {
        title: createColumnTitleWithUnit("col66", "Puncture Macadam"),
        dataIndex: "col66",
        key: "col66",
        width: 120,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col66", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: createColumnTitleWithUnit("col67", "xxmm sand Laying Course"),
        dataIndex: "col67",
        key: "col67",
        width: 130,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col67", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: createColumnTitleWithUnit("col68", "60mm Concrete paviors"),
        dataIndex: "col68",
        key: "col68",
        width: 130,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col68", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: createColumnTitleWithUnit("col69", "80mm Concrete paviors"),
        dataIndex: "col69",
        key: "col69",
        width: 130,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col69", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: "Actions",
        key: "actions",
        width: 110,
        fixed: "right" as const,
        align: "center" as const,
        render: (_, record: PlotDrivesRow) => (
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => deleteRow(record.key)}
            size="small"
          />
        ),
      },
    ],
    [baseColumns, createColumnTitleWithUnit]
  );

  // SHARED PERMEABLE - BINDER STAGE COLUMNS
  const sharedPermeableBinderColumns = React.useMemo(
    () => [
      ...baseColumns,
      {
        title: createColumnTitleWithUnit("col33", "Level and compact formation"),
        dataIndex: "col33",
        key: "col33",
        width: 110,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col33", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: createColumnTitleWithUnit("col34", "Terram Layer to Bottom and Sides permeable paving"),
        dataIndex: "col34",
        key: "col34",
        width: 130,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col34", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: createColumnTitleWithUnit("col35", "xxmm DB binder Running course"),
        dataIndex: "col35",
        key: "col35",
        width: 150,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col35", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: createColumnTitleWithUnit("col36", "xxmm thick 63-10mm Sub Base - Varied Depths"),
        dataIndex: "col36",
        key: "col36",
        width: 150,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col36", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: createColumnTitleWithUnit("col37", "xxmm thick 5-20mm clean aggregate sub base"),
        dataIndex: "col37",
        key: "col37",
        width: 150,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col37", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: createColumnTitleWithUnit("col38", "Permeable Geotextile Layer"),
        dataIndex: "col38",
        key: "col38",
        width: 120,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col38", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: "Actions",
        key: "actions",
        width: 110,
        fixed: "right" as const,
        align: "center" as const,
        render: (_, record: PlotDrivesRow) => (
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => deleteRow(record.key)}
            size="small"
          />
        ),
      },
    ],
    [baseColumns, createColumnTitleWithUnit]
  );

  // KERBS AND EDGINGS COLUMNS
  const kerbsAndEdgingsColumns = React.useMemo(
    () => [
      ...baseColumns,
      {
        title: createColumnTitleWithUnit("col39", "150x50mm PCC Edgings"),
        dataIndex: "col39",
        key: "col39",
        width: 110,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col39", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: createColumnTitleWithUnit("col40", "200x50mm PCC Edgings"),
        dataIndex: "col40",
        key: "col40",
        width: 110,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col40", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: createColumnTitleWithUnit("col41", "Timber Edgings"),
        dataIndex: "col41",
        key: "col41",
        width: 110,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col41", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: createColumnTitleWithUnit("col42", "PCC Kerbs"),
        dataIndex: "col42",
        key: "col42",
        width: 110,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col42", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: createColumnTitleWithUnit("col43", "xxxx Kerb"),
        dataIndex: "col43",
        key: "col43",
        width: 110,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col43", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: createColumnTitleWithUnit("col44", "xxxx Kerb"),
        dataIndex: "col44",
        key: "col44",
        width: 110,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col44", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: createColumnTitleWithUnit("col45", "xxxx Kerb"),
        dataIndex: "col45",
        key: "col45",
        width: 110,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col45", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: createColumnTitleWithUnit("col46", "Key Kerb"),
        dataIndex: "col46",
        key: "col46",
        width: 110,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col46", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: createColumnTitleWithUnit("col47", "Recessed C&F"),
        dataIndex: "col47",
        key: "col47",
        width: 110,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col47", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: "Actions",
        key: "actions",
        width: 110,
        fixed: "right" as const,
        align: "center" as const,
        render: (_: any, record: PlotDrivesRow) => (
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => deleteRow(record.key)}
            size="small"
          />
        ),
      },
    ],
    [baseColumns, createColumnTitleWithUnit]
  );

  // SHARED PERMEABLE - SURFACE STAGE COLUMNS
  const sharedPermeableSurfaceColumns = React.useMemo(
    () => [
      ...baseColumns,
      {
        title: createColumnTitleWithUnit("col70", "Puncture Macadam"),
        dataIndex: "col70",
        key: "col70",
        width: 120,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col70", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: createColumnTitleWithUnit("col71", "xxmm thick 2-6mm laying Aggregate"),
        dataIndex: "col71",
        key: "col71",
        width: 160,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col71", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: createColumnTitleWithUnit("col72", "60mm Concrete paviors"),
        dataIndex: "col72",
        key: "col72",
        width: 130,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col72", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: createColumnTitleWithUnit("col73", "80mm Concrete paviors"),
        dataIndex: "col73",
        key: "col73",
        width: 130,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col73", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: "Actions",
        key: "actions",
        width: 110,
        fixed: "right" as const,
        align: "center" as const,
        render: (_, record: PlotDrivesRow) => (
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => deleteRow(record.key)}
            size="small"
          />
        ),
      },
    ],
    [baseColumns, createColumnTitleWithUnit]
  );

  // ANCILLARY ITEMS - SURFACE STAGE COLUMNS
  const ancillaryItemsColumns = React.useMemo(
    () => [
      ...baseColumns,
      {
        title: createColumnTitleWithUnit("col74", "Adjust Ironwork"),
        dataIndex: "col74",
        key: "col74",
        width: 110,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col74", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: createColumnTitleWithUnit("col75", "Drive Demarcation"),
        dataIndex: "col75",
        key: "col75",
        width: 120,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col75", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: createColumnTitleWithUnit("col76", "Edging Remedials"),
        dataIndex: "col76",
        key: "col76",
        width: 120,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col76", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: createColumnTitleWithUnit("col77", "Kerb Remedials"),
        dataIndex: "col77",
        key: "col77",
        width: 120,
        render: (value: number, record: PlotDrivesRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "col77", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: "Actions",
        key: "actions",
        width: 110,
        fixed: "right" as const,
        align: "center" as const,
        render: (_: any, record: PlotDrivesRow) => (
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => deleteRow(record.key)}
            size="small"
          />
        ),
      },
    ],
    [baseColumns, createColumnTitleWithUnit]
  );

  // Calculate if horizontal and vertical scroll is needed
  const getScrollConfig = () => {
    const estimatedRowHeight = 50;
    const maxComfortableRows = 12;
    const needsVerticalScroll = data.length > maxComfortableRows;

    const scrollConfig: any = { x: true };

    if (needsVerticalScroll) {
      scrollConfig.y = maxComfortableRows * estimatedRowHeight;
    }

    return scrollConfig;
  };

  // BINDER STAGE TAB ITEMS
  const binderTabItems = [
    {
      key: "privateMacadam",
      label: "Private Macadam Drive",
      children: (
        <div>
          <div
            className="plot-drives-sticky-dimension"
            style={{
              position: "sticky",
              top:
                144 +
                document
                  .getElementsByClassName("plot-drives-sticky-header")?.[0]
                  ?.getBoundingClientRect?.()?.height +
                "px",
              backgroundColor: "#fff",
              zIndex: 98,
            }}
          ></div>

          {isTableLoading ? (
            <TableLoadingPlaceholder />
          ) : (
          <Table
            key={tableKey}
            dataSource={data}
            columns={addColumnCodes(macadamBinderColumns, effectiveColCodes)}
            pagination={false}
            rowSelection={{
              selectedRowKeys: selectedPlotDriveKeys,
              onChange: setSelectedPlotDriveKeys,
              fixed: "left",
              columnWidth: 48,
            }}
            scroll={getScrollConfig()}
            sticky={{
              offsetHeader:
                144 +
                document
                  .getElementsByClassName("plot-drives-sticky-header")?.[0]
                  ?.getBoundingClientRect?.()?.height +
                document
                  .getElementsByClassName("plot-drives-sticky-dimension")?.[0]
                  ?.getBoundingClientRect?.()?.height,
            }}
            size="middle"
            bordered
            className="plot-drives-table"
            summary={() => {
              if (data.length === 0) return null;
              const totals = calculateTotals();
              const cell = (val: number | undefined) => (
                <span style={{ color: "#000", display: "block", textAlign: "right" }}>
                  {(val ?? 0).toFixed(3)}
                </span>
              );
              return (
                <Table.Summary fixed="bottom">
                  <Table.Summary.Row
                    style={{ backgroundColor: "#FFF7E6", fontWeight: 600 }}
                  >
                    <Table.Summary.Cell index={0} colSpan={3} align="center"><span style={{ color: "#000" }}>Totals</span></Table.Summary.Cell>
                    <Table.Summary.Cell index={3}>{cell(totals.col1)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={4}>{cell(totals.col2)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={5}>{cell(totals.col3)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={6}>{cell(totals.col4)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={7}>{cell(totals.col5)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={8}>{cell(totals.col6)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={9}></Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              );
            }}
          />
          )}
        </div>
      ),
    },
    {
      key: "privateBlockPaved",
      label: "Private Block Paved Drive",
      children: (
        <div>
          {/* Sticky Dimension Card */}
          <div
            className="plot-drives-sticky-dimension-privateblock"
            style={{
              position: "sticky",
              top:
                144 +
                document
                  .getElementsByClassName("plot-drives-sticky-header")?.[0]
                  ?.getBoundingClientRect?.()?.height +
                "px",
              backgroundColor: "#fff",
              zIndex: 98,
            }}
          />

          {isTableLoading ? (
            <TableLoadingPlaceholder />
          ) : (
          <Table
            key={tableKey}
            dataSource={data}
            columns={addColumnCodes(blockPavedBinderColumns, effectiveColCodes)}
            pagination={false}
            rowSelection={{
              selectedRowKeys: selectedPlotDriveKeys,
              onChange: setSelectedPlotDriveKeys,
              fixed: "left",
              columnWidth: 48,
            }}
            scroll={getScrollConfig()}
            sticky={{
              offsetHeader:
                144 +
                document
                  .getElementsByClassName("plot-drives-sticky-header")?.[0]
                  ?.getBoundingClientRect?.()?.height +
                document
                  .getElementsByClassName(
                    "plot-drives-sticky-dimension-privateblock"
                  )?.[0]
                  ?.getBoundingClientRect?.()?.height,
            }}
            size="middle"
            bordered
            className="plot-drives-table"
            summary={() => {
              if (data.length === 0) return null;
              const totals = calculateTotals();
              const cell = (val: number | undefined) => (
                <span style={{ color: "#000", display: "block", textAlign: "right" }}>
                  {(val ?? 0).toFixed(3)}
                </span>
              );
              return (
                <Table.Summary fixed="bottom">
                  <Table.Summary.Row
                    style={{ backgroundColor: "#FFF7E6", fontWeight: 600 }}
                  >
                    <Table.Summary.Cell index={0} colSpan={3} align="center"><span style={{ color: "#000" }}>Totals</span></Table.Summary.Cell>
                    <Table.Summary.Cell index={3}>{cell(totals.col7)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={4}>{cell(totals.col8)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={5}>{cell(totals.col9)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={6}>{cell(totals.col10)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={7}>{cell(totals.col11)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={8}>{cell(totals.col12)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={9}></Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              );
            }}
          />
          )}
        </div>
      ),
    },
    {
      key: "privatePermeable",
      label: "Private Permeable Paving",
      children: (
        <div>
          {/* Sticky Dimension Card */}
          <div
            className="plot-drives-sticky-dimension-privatepermeable"
            style={{
              position: "sticky",
              top:
                144 +
                document
                  .getElementsByClassName("plot-drives-sticky-header")?.[0]
                  ?.getBoundingClientRect?.()?.height +
                "px",
              backgroundColor: "#fff",
              zIndex: 98,
            }}
          />

          {isTableLoading ? (
            <TableLoadingPlaceholder />
          ) : (
          <Table
            key={tableKey}
            dataSource={data}
            columns={addColumnCodes(permeableBinderColumns, effectiveColCodes)}
            pagination={false}
            rowSelection={{
              selectedRowKeys: selectedPlotDriveKeys,
              onChange: setSelectedPlotDriveKeys,
              fixed: "left",
              columnWidth: 48,
            }}
            scroll={getScrollConfig()}
            sticky={{
              offsetHeader:
                144 +
                document
                  .getElementsByClassName("plot-drives-sticky-header")?.[0]
                  ?.getBoundingClientRect?.()?.height +
                document
                  .getElementsByClassName(
                    "plot-drives-sticky-dimension-privatepermeable"
                  )?.[0]
                  ?.getBoundingClientRect?.()?.height,
            }}
            size="middle"
            bordered
            className="plot-drives-table"
            summary={() => {
              if (data.length === 0) return null;
              const totals = calculateTotals();
              const cell = (val: number | undefined) => (
                <span style={{ color: "#000", display: "block", textAlign: "right" }}>
                  {(val ?? 0).toFixed(3)}
                </span>
              );
              return (
                <Table.Summary fixed="bottom">
                  <Table.Summary.Row
                    style={{ backgroundColor: "#FFF7E6", fontWeight: 600 }}
                  >
                    <Table.Summary.Cell index={0} colSpan={3} align="center"><span style={{ color: "#000" }}>Totals</span></Table.Summary.Cell>
                    <Table.Summary.Cell index={3}>{cell(totals.col14)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={4}>{cell(totals.col15)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={5}>{cell(totals.col16)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={6}>{cell(totals.col17)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={7}>{cell(totals.col18)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={8}>{cell(totals.col19)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={9}></Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              );
            }}
          />
          )}
        </div>
      ),
    },
    {
      key: "sharedMacadam",
      label: "Shared Macadam Drive",
      children: (
        <div>
          {/* Sticky Dimension Card */}
          <div
            className="plot-drives-sticky-dimension-sharedmacadam"
            style={{
              position: "sticky",
              top:
                144 +
                document
                  .getElementsByClassName("plot-drives-sticky-header")?.[0]
                  ?.getBoundingClientRect?.()?.height +
                "px",
              backgroundColor: "#fff",
              zIndex: 98,
            }}
          />

          {isTableLoading ? (
            <TableLoadingPlaceholder />
          ) : (
          <Table
            key={tableKey}
            dataSource={data}
            columns={addColumnCodes(sharedMacadamBinderColumns, effectiveColCodes)}
            pagination={false}
            rowSelection={{
              selectedRowKeys: selectedPlotDriveKeys,
              onChange: setSelectedPlotDriveKeys,
              fixed: "left",
              columnWidth: 48,
            }}
            scroll={getScrollConfig()}
            sticky={{
              offsetHeader:
                144 +
                document
                  .getElementsByClassName("plot-drives-sticky-header")?.[0]
                  ?.getBoundingClientRect?.()?.height +
                document
                  .getElementsByClassName(
                    "plot-drives-sticky-dimension-sharedmacadam"
                  )?.[0]
                  ?.getBoundingClientRect?.()?.height,
            }}
            size="middle"
            bordered
            className="plot-drives-table"
            summary={() => {
              if (data.length === 0) return null;
              const totals = calculateTotals();
              const cell = (val: number | undefined) => (
                <span style={{ color: "#000", display: "block", textAlign: "right" }}>
                  {(val ?? 0).toFixed(3)}
                </span>
              );
              return (
                <Table.Summary fixed="bottom">
                  <Table.Summary.Row
                    style={{ backgroundColor: "#FFF7E6", fontWeight: 600 }}
                  >
                    <Table.Summary.Cell index={0} colSpan={3} align="center"><span style={{ color: "#000" }}>Totals</span></Table.Summary.Cell>
                    <Table.Summary.Cell index={3}>{cell(totals.col20)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={4}>{cell(totals.col21)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={5}>{cell(totals.col22)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={6}>{cell(totals.col23)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={7}>{cell(totals.col24)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={8}>{cell(totals.col25)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={9}></Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              );
            }}
          />
          )}
        </div>
      ),
    },
    {
      key: "sharedBlockPaved",
      label: "Shared Block Paved Drive",
      children: (
        <div>
          {/* Sticky Dimension Card */}
          <div
            className="plot-drives-sticky-dimension-sharedblock"
            style={{
              position: "sticky",
              top:
                144 +
                document
                  .getElementsByClassName("plot-drives-sticky-header")?.[0]
                  ?.getBoundingClientRect?.()?.height +
                "px",
              backgroundColor: "#fff",
              zIndex: 98,
            }}
          />

          {isTableLoading ? (
            <TableLoadingPlaceholder />
          ) : (
          <Table
            key={tableKey}
            dataSource={data}
            columns={addColumnCodes(sharedBlockBinderColumns, effectiveColCodes)}
            pagination={false}
            rowSelection={{
              selectedRowKeys: selectedPlotDriveKeys,
              onChange: setSelectedPlotDriveKeys,
              fixed: "left",
              columnWidth: 48,
            }}
            scroll={getScrollConfig()}
            sticky={{
              offsetHeader:
                144 +
                document
                  .getElementsByClassName("plot-drives-sticky-header")?.[0]
                  ?.getBoundingClientRect?.()?.height +
                document
                  .getElementsByClassName(
                    "plot-drives-sticky-dimension-sharedblock"
                  )?.[0]
                  ?.getBoundingClientRect?.()?.height,
            }}
            size="middle"
            bordered
            className="plot-drives-table"
            summary={() => {
              if (data.length === 0) return null;
              const totals = calculateTotals();
              const cell = (val: number | undefined) => (
                <span style={{ color: "#000", display: "block", textAlign: "right" }}>
                  {(val ?? 0).toFixed(3)}
                </span>
              );
              return (
                <Table.Summary fixed="bottom">
                  <Table.Summary.Row
                    style={{ backgroundColor: "#FFF7E6", fontWeight: 600 }}
                  >
                    <Table.Summary.Cell index={0} colSpan={3} align="center"><span style={{ color: "#000" }}>Totals</span></Table.Summary.Cell>
                    <Table.Summary.Cell index={3}>{cell(totals.col26)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={4}>{cell(totals.col27)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={5}>{cell(totals.col28)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={6}>{cell(totals.col29)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={7}>{cell(totals.col30)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={8}>{cell(totals.col31)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={9}></Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              );
            }}
          />
          )}
        </div>
      ),
    },
    {
      key: "sharedPermeable",
      label: "Shared Permeable Paving",
      children: (
        <div>
          {/* Sticky Dimension Card */}
          <div
            className="plot-drives-sticky-dimension-sharedpermeable"
            style={{
              position: "sticky",
              top:
                144 +
                document
                  .getElementsByClassName("plot-drives-sticky-header")?.[0]
                  ?.getBoundingClientRect?.()?.height +
                "px",
              backgroundColor: "#fff",
              zIndex: 98,
            }}
          />

          {isTableLoading ? (
            <TableLoadingPlaceholder />
          ) : (
          <Table
            key={tableKey}
            dataSource={data}
            columns={addColumnCodes(sharedPermeableBinderColumns, effectiveColCodes)}
            pagination={false}
            rowSelection={{
              selectedRowKeys: selectedPlotDriveKeys,
              onChange: setSelectedPlotDriveKeys,
              fixed: "left",
              columnWidth: 48,
            }}
            scroll={getScrollConfig()}
            sticky={{
              offsetHeader:
                144 +
                document
                  .getElementsByClassName("plot-drives-sticky-header")?.[0]
                  ?.getBoundingClientRect?.()?.height +
                document
                  .getElementsByClassName(
                    "plot-drives-sticky-dimension-sharedpermeable"
                  )?.[0]
                  ?.getBoundingClientRect?.()?.height,
            }}
            size="middle"
            bordered
            className="plot-drives-table"
            summary={() => {
              if (data.length === 0) return null;
              const totals = calculateTotals();
              const cell = (val: number | undefined) => (
                <span style={{ color: "#000", display: "block", textAlign: "right" }}>
                  {(val ?? 0).toFixed(3)}
                </span>
              );
              return (
                <Table.Summary fixed="bottom">
                  <Table.Summary.Row
                    style={{ backgroundColor: "#FFF7E6", fontWeight: 600 }}
                  >
                    <Table.Summary.Cell index={0} colSpan={3} align="center"><span style={{ color: "#000" }}>Totals</span></Table.Summary.Cell>
                    <Table.Summary.Cell index={3}>{cell(totals.col33)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={4}>{cell(totals.col34)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={5}>{cell(totals.col35)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={6}>{cell(totals.col36)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={7}>{cell(totals.col37)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={8}>{cell(totals.col38)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={9}></Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              );
            }}
          />
          )}
        </div>
      ),
    },
    {
      key: "kerbsAndEdgings",
      label: "Kerbs and Edgings",
      children: (
        <div>
          {/* Sticky Dimension Card */}
          <div
            className="plot-drives-sticky-dimension-kerbsandedgings"
            style={{
              position: "sticky",
              top:
                144 +
                document
                  .getElementsByClassName("plot-drives-sticky-header")?.[0]
                  ?.getBoundingClientRect?.()?.height +
                "px",
              backgroundColor: "#fff",
              zIndex: 98,
            }}
          />

          {isTableLoading ? (
            <TableLoadingPlaceholder />
          ) : (
          <Table
            key={tableKey}
            dataSource={data}
            columns={addColumnCodes(kerbsAndEdgingsColumns, effectiveColCodes)}
            pagination={false}
            rowSelection={{
              selectedRowKeys: selectedPlotDriveKeys,
              onChange: setSelectedPlotDriveKeys,
              fixed: "left",
              columnWidth: 48,
            }}
            scroll={getScrollConfig()}
            sticky={{
              offsetHeader:
                144 +
                document
                  .getElementsByClassName("plot-drives-sticky-header")?.[0]
                  ?.getBoundingClientRect?.()?.height +
                document
                  .getElementsByClassName(
                    "plot-drives-sticky-dimension-kerbsandedgings"
                  )?.[0]
                  ?.getBoundingClientRect?.()?.height,
            }}
            size="middle"
            bordered
            className="plot-drives-table"
            summary={() => {
              if (data.length === 0) return null;
              const totals = calculateTotals();
              const cell = (val: number | undefined) => (
                <span style={{ color: "#000", display: "block", textAlign: "right" }}>
                  {(val ?? 0).toFixed(3)}
                </span>
              );
              return (
                <Table.Summary fixed="bottom">
                  <Table.Summary.Row
                    style={{ backgroundColor: "#FFF7E6", fontWeight: 600 }}
                  >
                    <Table.Summary.Cell index={0} colSpan={3} align="center"><span style={{ color: "#000" }}>Totals</span></Table.Summary.Cell>
                    <Table.Summary.Cell index={3}>{cell(totals.col39)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={4}>{cell(totals.col40)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={5}>{cell(totals.col41)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={6}>{cell(totals.col42)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={7}>{cell(totals.col43)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={8}>{cell(totals.col44)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={9}>{cell(totals.col45)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={10}>{cell(totals.col46)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={11}>{cell(totals.col47)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={12}></Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              );
            }}
          />
          )}
        </div>
      ),
    },
  ];

  // SURFACE STAGE TAB ITEMS
  const surfaceTabItems = [
    {
      key: "privateMacadam",
      label: "Private Macadam Drive",
      children: (
        <div>
          {/* Sticky Dimension Card */}
          <div
            className="plot-drives-sticky-dimension-privatemacadam-surface"
            style={{
              position: "sticky",
              top:
                144 +
                document
                  .getElementsByClassName("plot-drives-sticky-header")?.[0]
                  ?.getBoundingClientRect?.()?.height +
                "px",
              backgroundColor: "#fff",
              zIndex: 98,
            }}
          />

          {isTableLoading ? (
            <TableLoadingPlaceholder />
          ) : (
          <Table
            key={tableKey}
            dataSource={data}
            columns={addColumnCodes(macadamSurfaceColumns, effectiveColCodes)}
            pagination={false}
            rowSelection={{
              selectedRowKeys: selectedPlotDriveKeys,
              onChange: setSelectedPlotDriveKeys,
              fixed: "left",
              columnWidth: 48,
            }}
            scroll={getScrollConfig()}
            sticky={{
              offsetHeader:
                144 +
                document
                  .getElementsByClassName("plot-drives-sticky-header")?.[0]
                  ?.getBoundingClientRect?.()?.height +
                document
                  .getElementsByClassName(
                    "plot-drives-sticky-dimension-privatemacadam-surface"
                  )?.[0]
                  ?.getBoundingClientRect?.()?.height,
            }}
            size="middle"
            bordered
            className="plot-drives-table"
            summary={() => {
              if (data.length === 0) return null;
              const totals = calculateTotals();
              const cell = (val: number | undefined) => (
                <span style={{ color: "#000", display: "block", textAlign: "right" }}>
                  {(val ?? 0).toFixed(3)}
                </span>
              );
              return (
                <Table.Summary fixed="bottom">
                  <Table.Summary.Row
                    style={{ backgroundColor: "#FFF7E6", fontWeight: 600 }}
                  >
                    <Table.Summary.Cell index={0} colSpan={3} align="center"><span style={{ color: "#000" }}>Totals</span></Table.Summary.Cell>
                    <Table.Summary.Cell index={3}>{cell(totals.col48)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={4}>{cell(totals.col49)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={5}>{cell(totals.col50)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={6}>{cell(totals.col51)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={7}>{cell(totals.col52)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={8}></Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              );
            }}
          />
          )}
        </div>
      ),
    },
    {
      key: "privateBlockPaved",
      label: "Private Block Paved Drive",
      children: (
        <div>
          {/* Sticky Dimension Card */}
          <div
            className="plot-drives-sticky-dimension-privateblock-surface"
            style={{
              position: "sticky",
              top:
                144 +
                document
                  .getElementsByClassName("plot-drives-sticky-header")?.[0]
                  ?.getBoundingClientRect?.()?.height +
                "px",
              backgroundColor: "#fff",
              zIndex: 98,
            }}
          />

          {isTableLoading ? (
            <TableLoadingPlaceholder />
          ) : (
          <Table
            key={tableKey}
            dataSource={data}
            columns={addColumnCodes(blockPavedSurfaceColumns, effectiveColCodes)}
            pagination={false}
            rowSelection={{
              selectedRowKeys: selectedPlotDriveKeys,
              onChange: setSelectedPlotDriveKeys,
              fixed: "left",
              columnWidth: 48,
            }}
            scroll={getScrollConfig()}
            sticky={{
              offsetHeader:
                144 +
                document
                  .getElementsByClassName("plot-drives-sticky-header")?.[0]
                  ?.getBoundingClientRect?.()?.height +
                document
                  .getElementsByClassName(
                    "plot-drives-sticky-dimension-privateblock-surface"
                  )?.[0]
                  ?.getBoundingClientRect?.()?.height,
            }}
            size="middle"
            bordered
            className="plot-drives-table"
            summary={() => {
              if (data.length === 0) return null;
              const totals = calculateTotals();
              const cell = (val: number | undefined) => (
                <span style={{ color: "#000", display: "block", textAlign: "right" }}>
                  {(val ?? 0).toFixed(3)}
                </span>
              );
              return (
                <Table.Summary fixed="bottom">
                  <Table.Summary.Row
                    style={{ backgroundColor: "#FFF7E6", fontWeight: 600 }}
                  >
                    <Table.Summary.Cell index={0} colSpan={3} align="center"><span style={{ color: "#000" }}>Totals</span></Table.Summary.Cell>
                    <Table.Summary.Cell index={3}>{cell(totals.col53)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={4}>{cell(totals.col54)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={5}>{cell(totals.col55)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={6}>{cell(totals.col56)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={7}>{cell(totals.col57)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={8}></Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              );
            }}
          />
          )}
        </div>
      ),
    },
    {
      key: "privatePermeable",
      label: "Private Permeable Paving",
      children: (
        <div>
          {/* Sticky Dimension Card */}
          <div
            className="plot-drives-sticky-dimension-privatepermeable-surface"
            style={{
              position: "sticky",
              top:
                144 +
                document
                  .getElementsByClassName("plot-drives-sticky-header")?.[0]
                  ?.getBoundingClientRect?.()?.height +
                "px",
              backgroundColor: "#fff",
              zIndex: 98,
            }}
          />

          {isTableLoading ? (
            <TableLoadingPlaceholder />
          ) : (
          <Table
            key={tableKey}
            dataSource={data}
            columns={addColumnCodes(permeableSurfaceColumns, effectiveColCodes)}
            pagination={false}
            rowSelection={{
              selectedRowKeys: selectedPlotDriveKeys,
              onChange: setSelectedPlotDriveKeys,
              fixed: "left",
              columnWidth: 48,
            }}
            scroll={getScrollConfig()}
            sticky={{
              offsetHeader:
                144 +
                document
                  .getElementsByClassName("plot-drives-sticky-header")?.[0]
                  ?.getBoundingClientRect?.()?.height +
                document
                  .getElementsByClassName(
                    "plot-drives-sticky-dimension-privatepermeable-surface"
                  )?.[0]
                  ?.getBoundingClientRect?.()?.height,
            }}
            size="middle"
            bordered
            className="plot-drives-table"
            summary={() => {
              if (data.length === 0) return null;
              const totals = calculateTotals();
              const cell = (val: number | undefined) => (
                <span style={{ color: "#000", display: "block", textAlign: "right" }}>
                  {(val ?? 0).toFixed(3)}
                </span>
              );
              return (
                <Table.Summary fixed="bottom">
                  <Table.Summary.Row
                    style={{ backgroundColor: "#FFF7E6", fontWeight: 600 }}
                  >
                    <Table.Summary.Cell index={0} colSpan={3} align="center"><span style={{ color: "#000" }}>Totals</span></Table.Summary.Cell>
                    <Table.Summary.Cell index={3}>{cell(totals.col58)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={4}>{cell(totals.col59)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={5}>{cell(totals.col60)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={6}>{cell(totals.col61)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={7}></Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              );
            }}
          />
          )}
        </div>
      ),
    },
    {
      key: "sharedMacadam",
      label: "Shared Macadam Drive",
      children: (
        <div>
          {/* Sticky Dimension Card */}
          <div
            className="plot-drives-sticky-dimension-sharedmacadam-surface"
            style={{
              position: "sticky",
              top:
                144 +
                document
                  .getElementsByClassName("plot-drives-sticky-header")?.[0]
                  ?.getBoundingClientRect?.()?.height +
                "px",
              backgroundColor: "#fff",
              zIndex: 98,
            }}
          />

          {isTableLoading ? (
            <TableLoadingPlaceholder />
          ) : (
          <Table
            key={tableKey}
            dataSource={data}
            columns={addColumnCodes(sharedMacadamSurfaceColumns, effectiveColCodes)}
            pagination={false}
            rowSelection={{
              selectedRowKeys: selectedPlotDriveKeys,
              onChange: setSelectedPlotDriveKeys,
              fixed: "left",
              columnWidth: 48,
            }}
            scroll={getScrollConfig()}
            sticky={{
              offsetHeader:
                144 +
                document
                  .getElementsByClassName("plot-drives-sticky-header")?.[0]
                  ?.getBoundingClientRect?.()?.height +
                document
                  .getElementsByClassName(
                    "plot-drives-sticky-dimension-sharedmacadam-surface"
                  )?.[0]
                  ?.getBoundingClientRect?.()?.height,
            }}
            size="middle"
            bordered
            className="plot-drives-table"
            summary={() => {
              if (data.length === 0) return null;
              const totals = calculateTotals();
              const cell = (val: number | undefined) => (
                <span style={{ color: "#000", display: "block", textAlign: "right" }}>
                  {(val ?? 0).toFixed(3)}
                </span>
              );
              return (
                <Table.Summary fixed="bottom">
                  <Table.Summary.Row
                    style={{ backgroundColor: "#FFF7E6", fontWeight: 600 }}
                  >
                    <Table.Summary.Cell index={0} colSpan={3} align="center"><span style={{ color: "#000" }}>Totals</span></Table.Summary.Cell>
                    <Table.Summary.Cell index={3}>{cell(totals.col62)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={4}>{cell(totals.col63)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={5}>{cell(totals.col64)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={6}>{cell(totals.col65)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={7}></Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              );
            }}
          />
          )}
        </div>
      ),
    },
    {
      key: "sharedBlockPaved",
      label: "Shared Block Paving",
      children: (
        <div>
          {/* Sticky Dimension Card */}
          <div
            className="plot-drives-sticky-dimension-sharedblock-surface"
            style={{
              position: "sticky",
              top:
                144 +
                document
                  .getElementsByClassName("plot-drives-sticky-header")?.[0]
                  ?.getBoundingClientRect?.()?.height +
                "px",
              backgroundColor: "#fff",
              zIndex: 98,
            }}
          />

          {isTableLoading ? (
            <TableLoadingPlaceholder />
          ) : (
          <Table
            key={tableKey}
            dataSource={data}
            columns={addColumnCodes(sharedBlockSurfaceColumns, effectiveColCodes)}
            pagination={false}
            rowSelection={{
              selectedRowKeys: selectedPlotDriveKeys,
              onChange: setSelectedPlotDriveKeys,
              fixed: "left",
              columnWidth: 48,
            }}
            scroll={getScrollConfig()}
            sticky={{
              offsetHeader:
                144 +
                document
                  .getElementsByClassName("plot-drives-sticky-header")?.[0]
                  ?.getBoundingClientRect?.()?.height +
                document
                  .getElementsByClassName(
                    "plot-drives-sticky-dimension-sharedblock-surface"
                  )?.[0]
                  ?.getBoundingClientRect?.()?.height,
            }}
            size="middle"
            bordered
            className="plot-drives-table"
            summary={() => {
              if (data.length === 0) return null;
              const totals = calculateTotals();
              const cell = (val: number | undefined) => (
                <span style={{ color: "#000", display: "block", textAlign: "right" }}>
                  {(val ?? 0).toFixed(3)}
                </span>
              );
              return (
                <Table.Summary fixed="bottom">
                  <Table.Summary.Row
                    style={{ backgroundColor: "#FFF7E6", fontWeight: 600 }}
                  >
                    <Table.Summary.Cell index={0} colSpan={3} align="center"><span style={{ color: "#000" }}>Totals</span></Table.Summary.Cell>
                    <Table.Summary.Cell index={3}>{cell(totals.col66)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={4}>{cell(totals.col67)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={5}>{cell(totals.col68)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={6}>{cell(totals.col69)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={7}></Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              );
            }}
          />
          )}
        </div>
      ),
    },
    {
      key: "sharedPermeable",
      label: "Shared Permeable Paving",
      children: (
        <div>
          {/* Sticky Dimension Card */}
          <div
            className="plot-drives-sticky-dimension-sharedpermeable-surface"
            style={{
              position: "sticky",
              top:
                144 +
                document
                  .getElementsByClassName("plot-drives-sticky-header")?.[0]
                  ?.getBoundingClientRect?.()?.height +
                "px",
              backgroundColor: "#fff",
              zIndex: 98,
            }}
          />

          {isTableLoading ? (
            <TableLoadingPlaceholder />
          ) : (
          <Table
            key={tableKey}
            dataSource={data}
            columns={addColumnCodes(sharedPermeableSurfaceColumns, effectiveColCodes)}
            pagination={false}
            rowSelection={{
              selectedRowKeys: selectedPlotDriveKeys,
              onChange: setSelectedPlotDriveKeys,
              fixed: "left",
              columnWidth: 48,
            }}
            scroll={getScrollConfig()}
            sticky={{
              offsetHeader:
                144 +
                document
                  .getElementsByClassName("plot-drives-sticky-header")?.[0]
                  ?.getBoundingClientRect?.()?.height +
                document
                  .getElementsByClassName(
                    "plot-drives-sticky-dimension-sharedpermeable-surface"
                  )?.[0]
                  ?.getBoundingClientRect?.()?.height,
            }}
            size="middle"
            bordered
            className="plot-drives-table"
            summary={() => {
              if (data.length === 0) return null;
              const totals = calculateTotals();
              const cell = (val: number | undefined) => (
                <span style={{ color: "#000", display: "block", textAlign: "right" }}>
                  {(val ?? 0).toFixed(3)}
                </span>
              );
              return (
                <Table.Summary fixed="bottom">
                  <Table.Summary.Row
                    style={{ backgroundColor: "#FFF7E6", fontWeight: 600 }}
                  >
                    <Table.Summary.Cell index={0} colSpan={3} align="center"><span style={{ color: "#000" }}>Totals</span></Table.Summary.Cell>
                    <Table.Summary.Cell index={3}>{cell(totals.col70)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={4}>{cell(totals.col71)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={5}>{cell(totals.col72)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={6}>{cell(totals.col73)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={7}></Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              );
            }}
          />
          )}
        </div>
      ),
    },
    {
      key: "ancillaryItems",
      label: "Ancillary Items",
      children: (
        <div>
          {/* Sticky Dimension Card */}
          <div
            className="plot-drives-sticky-dimension-ancillaryitems"
            style={{
              position: "sticky",
              top:
                144 +
                document
                  .getElementsByClassName("plot-drives-sticky-header")?.[0]
                  ?.getBoundingClientRect?.()?.height +
                "px",
              backgroundColor: "#fff",
              zIndex: 98,
            }}
          />

          {isTableLoading ? (
            <TableLoadingPlaceholder />
          ) : (
          <Table
            key={tableKey}
            dataSource={data}
            columns={addColumnCodes(ancillaryItemsColumns, effectiveColCodes)}
            pagination={false}
            rowSelection={{
              selectedRowKeys: selectedPlotDriveKeys,
              onChange: setSelectedPlotDriveKeys,
              fixed: "left",
              columnWidth: 48,
            }}
            scroll={getScrollConfig()}
            sticky={{
              offsetHeader:
                144 +
                document
                  .getElementsByClassName("plot-drives-sticky-header")?.[0]
                  ?.getBoundingClientRect?.()?.height +
                document
                  .getElementsByClassName(
                    "plot-drives-sticky-dimension-ancillaryitems"
                  )?.[0]
                  ?.getBoundingClientRect?.()?.height,
            }}
            size="middle"
            bordered
            className="plot-drives-table"
            summary={() => {
              if (data.length === 0) return null;
              const totals = calculateTotals();
              const cell = (val: number | undefined) => (
                <span style={{ color: "#000", display: "block", textAlign: "right" }}>
                  {(val ?? 0).toFixed(3)}
                </span>
              );
              return (
                <Table.Summary fixed="bottom">
                  <Table.Summary.Row
                    style={{ backgroundColor: "#FFF7E6", fontWeight: 600 }}
                  >
                    <Table.Summary.Cell index={0} colSpan={3} align="center"><span style={{ color: "#000" }}>Totals</span></Table.Summary.Cell>
                    <Table.Summary.Cell index={3}>{cell(totals.col74)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={4}>{cell(totals.col75)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={5}>{cell(totals.col76)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={6}>{cell(totals.col77)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={7}></Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              );
            }}
          />
          )}
        </div>
      ),
    },
  ];

  return (
    <MainLayout showFooter={false}>
      <Card
        className="shadow-sm plot-drives-card"
        bodyStyle={{ padding: 0 }}
        style={{ borderRadius: "8px", overflow: "hidden" }}
      >
        {/* Sticky Header with Title, Drive Stage, and Tabs */}
        <div
          className="plot-drives-sticky-header"
          style={{
            position: "sticky",
            top: "144px",
            backgroundColor: "#fff",
            zIndex: 98,
            borderRadius: "8px 8px 0 0",
            isolation: "isolate",
          }}
        >
          {/* Header Section */}
          <div
            className="px-6 pt-6 pb-4"
            style={{ borderBottom: "1px solid #e5e7eb" }}
          >
            <div className="flex justify-between items-center mb-4">
              <div>
                <Title
                  level={3}
                  style={{
                    fontWeight: 600,
                    fontSize: "20px",
                    color: "#111827",
                    marginBottom: "8px",
                  }}
                >
                  Plot Drives
                </Title>
                <Text
                  type="secondary"
                  style={{ fontSize: "14px", color: "#6B7280" }}
                >
                  Configure drive measurements - choose between private and
                  shared drives, each with multiple construction types.
                </Text>
              </div>
              {activeTab !== "ancillaryItems" && (
                <Button
                  icon={<SettingOutlined />}
                  onClick={() => setConfigModalVisible(true)}
                  type="default"
                  className="configure-headers-btn"
                  style={{
                    height: "32px",
                    borderRadius: "6px",
                    fontWeight: 500,
                    borderColor: "#1890ff",
                    color: "#1890ff",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    flexShrink: 0,
                    marginTop: "18px",
                  }}
                >
                  Configure Headers
                </Button>
              )}
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Text style={{ fontWeight: 500 }}>Drive Stage:</Text>
                <Select
                  value={driveStage}
                  onChange={(value: "binder" | "surface") => {
                    setDriveStage(value);
                    // binderTabItems' last tab is "kerbsAndEdgings" but surfaceTabItems'
                    // is "ancillaryItems" — the two tab sets aren't 1:1, so staying on
                    // whichever tab was active left the page with no matching tab (blank
                    // pane, no tab button showing selected) whenever that tab didn't
                    // exist in the other stage. Always land on the first tab instead.
                    const firstTab = (value === "binder" ? binderTabItems : surfaceTabItems)[0]?.key;
                    if (firstTab) setActiveTab(firstTab);
                  }}
                  style={{ width: 220 }}
                  size="middle"
                  className="supplier-select"
                >
                  <Option value="binder">Drives to Binder</Option>
                  <Option value="surface">Drives Surface Course</Option>
                </Select>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                {selectedPlotDriveKeys.length > 0 && (
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    onClick={deleteSelectedRows}
                    style={{
                      height: "32px",
                      borderRadius: "6px",
                      fontWeight: 500,
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    Delete Selected ({selectedPlotDriveKeys.length})
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Tab Buttons */}
          <div
            className="plot-drives-sticky-tabs px-6"
            style={{ paddingTop: "24px", paddingBottom: "0px" }}
          >
            <div
              style={{
                marginBottom: "0px",
                display: "flex",
                gap: "4px",
                flexWrap: "wrap",
              }}
            >
              {(driveStage === "binder" ? binderTabItems : surfaceTabItems).map(
                (tab) => (
                  <Button
                    key={tab.key}
                    type={activeTab === tab.key ? "primary" : "default"}
                    onClick={() => setActiveTab(tab.key)}
                    className={
                      activeTab === tab.key
                        ? "active-tab-button"
                        : "inactive-tab-button"
                    }
                    style={{
                      backgroundColor:
                        activeTab === tab.key ? COLORS.primary : "#E1E6EF",
                      borderColor:
                        activeTab === tab.key ? COLORS.primary : "#E1E6EF",
                      color: activeTab === tab.key ? "#fff" : "#6B7280",
                      fontWeight: 500,
                      borderRadius: "6px 6px 0 0",
                      height: "38px",
                      paddingLeft: "12px",
                      paddingRight: "12px",
                      borderBottom:
                        activeTab === tab.key ? "none" : "1px solid #E1E6EF",
                      position: "relative",
                      zIndex: activeTab === tab.key ? 2 : 1,
                    }}
                  >
                    {tab.label}
                  </Button>
                )
              )}
            </div>
          </div>
        </div>

        {/* Content Section - Tables */}
        <div
          className="px-6 pb-6 plot-drives-content-section"
          style={{
            paddingTop: "0px",
            position: "relative",
            zIndex: 1,
            marginTop: "0px",
          }}
        >
          {/* Tab Content */}
          {(driveStage === "binder" ? binderTabItems : surfaceTabItems).map(
            (tab) =>
              activeTab === tab.key && <div key={tab.key}>{tab.children}</div>
          )}

          {/* Add New Row Button */}
          <div style={{ marginTop: "16px" }}>
            <Button
              type="text"
              icon={<PlusOutlined style={{ color: COLORS.primary }} />}
              onClick={addRow}
              style={{
                color: COLORS.primary,
                fontWeight: 500,
                padding: "4px 8px",
              }}
            >
              Add New Row
            </Button>
          </div>
        </div>

        {/* Footer with action buttons */}
        <div style={{ borderTop: "1px solid #e5e7eb" }}>
          <div className="px-6 py-4 flex justify-end gap-3">
            <Button
              onClick={handleSaveAndExit}
              style={{
                borderColor: COLORS.primary,
                color: COLORS.primary,
              }}
            >
              Save and Exit
            </Button>
            <Button
              type="primary"
              onClick={handleSaveAndContinue}
              style={{
                backgroundColor: COLORS.primary,
                borderColor: COLORS.primary,
              }}
            >
              Save & Continue <ArrowRightOutlined />
            </Button>
          </div>
        </div>

        {/* Excel Import Modal - Hidden, only for modal functionality */}
        <div style={{ display: "none" }}>
          <ExcelImportExport
            onExport={handleExportExcel}
            onImport={handleImportExcel}
            onDownloadTemplate={handleDownloadTemplate}
            importLoading={importLoading}
            importModalVisible={importModalVisible}
            setImportModalVisible={setImportModalVisible}
            hasData={data.length > 0}
          />
        </div>
      </Card>

      <style>{`
        .plot-drives-card.ant-card {
          border-radius: 8px !important;
          overflow: visible !important;
        }

        .plot-drives-card.ant-card .ant-card-body {
          border-radius: 8px !important;
          overflow: visible !important;
          position: relative !important;
        }

        .plot-drives-sticky-header {
          box-shadow: none;
          will-change: transform;
          transform: translateZ(0);
          z-index: 98 !important;
        }

        .plot-drives-sticky-tabs {
          border-bottom: none;
          min-height: 40px;
        }

        .plot-drives-card {
          position: relative;
          height: auto;
          overflow: visible !important;
        }

        .plot-drives-card .ant-card-body {
          overflow: visible !important;
        }

        .plot-drives-content-section {
          clip-path: inset(0px -100vw -100vh -100vw);
        }

        .plot-drives-table .ant-table-body {
          margin-top: 0 !important;
        }

        .active-tab-button {
          border-bottom: none !important;
        }

        .inactive-tab-button {
          border-bottom: none !important;
        }

        .plot-drives-table {
          font-size: 14px;
          margin-top: 0 !important;
        }

        .plot-drives-table .ant-table-wrapper {
          margin-top: -1px !important;
        }

        .plot-drives-table .ant-table-container {
          margin-top: 0 !important;
        }

        .plot-drives-table .ant-table {
          border: none !important;
          margin-top: 0 !important;
        }

        .plot-drives-table .ant-table-container {
          border: 1px solid #e5e7eb !important;
          border-top: none !important;
          border-radius: 0 !important;
        }

        .plot-drives-table .ant-table-thead > tr > th {
          min-width: 110px !important;
          background-color: ${COLORS.tableHeaderBg} !important;
          font-weight: 600 !important;
          color: #000 !important;
          border-right: 1px solid #e5e7eb !important;
          border-bottom: 1px solid #e5e7eb !important;
          border-left: none !important;
          border-top: none !important;
          padding: 24px 8px 10px 8px !important;
          text-align: center !important;
          font-size: 13px !important;
          will-change: transform !important;
          transform: translateZ(0) !important;
          z-index: 97 !important;
        }

        .plot-drives-table .ant-table-thead > tr > th:first-child {
          border-left: none !important;
        }

        .plot-drives-table .ant-table-thead > tr > th.ant-table-cell-fix-left {
          z-index: 99 !important;
          background-color: ${COLORS.tableHeaderBg} !important;
        }

        .plot-drives-table .ant-table-tbody > tr > td.ant-table-cell-fix-left {
          z-index: 2 !important;
          background-color: #fff !important;
        }

        .plot-drives-table .ant-table-tbody > tr:hover > td.ant-table-cell-fix-left {
          background-color: #fafafa !important;
        }

        .plot-drives-table .ant-table-thead > tr > th.ant-table-cell-fix-right {
          z-index: 100 !important;
          background-color: ${COLORS.tableHeaderBg} !important;
        }

        .plot-drives-table .ant-table-tbody > tr > td.ant-table-cell-fix-right {
          z-index: 2 !important;
          background-color: #fff !important;
        }

        .plot-drives-table .ant-table-tbody > tr:hover > td.ant-table-cell-fix-right {
          background-color: #fafafa !important;
        }

        /* The generic min-width:110px above also matches the rowSelection
           checkbox column since it's a plain th/td. Keep it pinned to the
           rowSelection columnWidth instead of being stretched. */
        .plot-drives-table .ant-table-thead > tr > th.ant-table-selection-column,
        .plot-drives-table .ant-table-tbody > tr > td.ant-table-selection-column {
          min-width: 48px !important;
          width: 48px !important;
        }

        .plot-drives-table .ant-table-tbody > tr > td {
          min-width: 110px !important;
          padding: 6px 8px !important;
          border-right: 1px solid #e5e7eb !important;
          border-bottom: 1px solid #e5e7eb !important;
          border-left: none !important;
          border-top: none !important;
          background-color: #fff;
        }

        .plot-drives-table .ant-table-tbody > tr > td:first-child {
          border-left: none !important;
        }

        .plot-drives-table .ant-table-tbody > tr:hover > td {
          background-color: #fafafa !important;
        }

        .plot-drives-table .ant-table-tbody > tr.ant-table-row-selected > td {
          background-color: #fff !important;
        }

        .plot-drives-table .ant-input {
          border: 1px solid #d9d9d9 !important;
          border-radius: 4px !important;
          padding: 4px 11px !important;
          font-size: 14px !important;
          height: 32px !important;
        }

        /* Center-align text inputs by default (for Plot No, Housetype) */
        .plot-drives-table .ant-input[type="text"] {
          text-align: center !important;
        }

        /* Right-align NumberInput components (exclude checkbox, Plot No, Housetype columns) */
        .plot-drives-table td:not(:nth-child(1)):not(:nth-child(2)):not(:nth-child(3)) .ant-input[type="text"] {
          text-align: right !important;
        }

        .plot-drives-table .ant-input[type="number"] {
          text-align: right !important;
        }

        .plot-drives-table .ant-input:focus {
          border-color: ${COLORS.primary} !important;
          box-shadow: 0 0 0 2px rgba(22, 126, 249, 0.1) !important;
        }

        .plot-drives-table .ant-input-number {
          width: 100%;
        }

        .plot-drives-table .ant-table-summary > tr > td {
          background-color: #FFF7E6 !important;
          border-right: 1px solid #e5e7eb !important;
          border-bottom: none !important;
          border-left: none !important;
          border-top: none !important;
          padding: 10px 8px !important;
          font-weight: 600 !important;
        }

        .plot-drives-table .ant-table-summary > tr > td:first-child {
          border-left: none !important;
        }

        .plot-drives-table .ant-btn-text.ant-btn-dangerous {
          color: #ff4d4f !important;
        }

        .plot-drives-table .ant-btn-text.ant-btn-dangerous:hover {
          background-color: #fff1f0 !important;
        }
      `}</style>

      {/* Column Configuration Modal - Shows fields for active tab only */}
      <ColumnConfigModal
        title="Configure Headers"
        open={configModalVisible}
        onClose={() => {
          // Reset temp values back to actual values when modal closes without applying
          setTempDimensionValues({ ...dimensionValues });
          setTempColumnHeaders({ ...columnHeaders });
          setConfigModalVisible(false);
        }}
        onApply={handleConfigApply}
        width={500}
        isViewMode={tender.isViewMode}
      >
        {/* Render dimension fields for the currently active tab */}
        {(() => {
          // Convert tab key to drive type: e.g., "privateMacadam" + "binder" -> "privateMacadamBinder"
          // For "BlockPaved" tabs, use "Block" in the drive type name
          const tabToDriveType =
            activeTab.replace("Paved", "") +
            driveStage.charAt(0).toUpperCase() +
            driveStage.slice(1);
          return getDimensionFieldsCard(tabToDriveType);
        })()}
      </ColumnConfigModal>

      <DeleteConfirmationModal
        visible={deleteModalVisible}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        title={keysToDelete.length > 1 ? `Are you sure you want to delete ${keysToDelete.length} records?` : undefined}
      />
    </MainLayout>
  );
};

export default PlotDrives;
